import React, { useEffect, useMemo, useState } from 'react'
import { supabase, SUPABASE_URL } from './supabaseClient'
import SaldoFacturas from './SaldoFacturas'
import { useAuth } from './AuthContext'
import {
  ShoppingBag, Plus, X, FileText, Eye, Upload, CheckCircle, AlertCircle,
  Clock, Search, Filter, Calendar, RefreshCw, Pencil, Trash2, ShieldOff, PackageX, AlertTriangle,
  FileSearch, Zap,
} from 'lucide-react'
import { parseFaturaPdfFile } from './utils/parseFatura'
import { lerTudo } from './lerTudo'

import { C } from './theme'
import { SLOTS_TRANSITO } from './constants'

// Normaliza nome de produto para match aproximado (case/acentos insensível)
function norm(s) {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()
}

// Lista de bancos PT — consistente com Financeiro.jsx (autocomplete + opção livre)
const BANCOS_PT = [
  'Millennium BCP',
  'Santander Totta',
  'Caixa Geral de Depósitos',
  'Novo Banco',
  'BPI',
  'Crédito Agrícola',
  'Montepio',
  'EuroBic',
  'Banco CTT',
  'ActivoBank',
  'Bankinter',
  'BIG',
  'Banco Best',
  'N26',
  'Revolut',
  'Wise',
  'Conta interna',
]

const ESTADO_INFO = {
  pendente:    { label: 'Pendente',    cor: C.warning, bg: '#fff8e1' },
  // A 1.a prestacao ao fornecedor nem sempre e metade: no acordo Plus Skill sao
  // 20%, e cada compra guarda a sua em sinal_percentagem. Escrever "50% Pago"
  // aqui fazia a etiqueta mentir sobre uma factura de 10 mil euros — dizia
  // metade paga quando estavam pagos dois mil. A percentagem certa e calculada
  // por linha; este rotulo e so o que sobra quando ela nao se sabe.
  pago_1a:     { label: '1.ª paga',    cor: C.primary, bg: '#eff6fc' },
  pago_total:  { label: '100% Pago',   cor: C.success, bg: '#e8f5e9' },
  cancelado:   { label: 'Cancelado',   cor: C.danger,  bg: '#fde7e9' },
}
const fmt2 = (n) => Number(n || 0).toFixed(2)
const fmtEur = (n) => Number(n || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : '—'

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

// Valor efectivo da factura: valor final (com portes) quando a fase ja e final, senao a previsao
function valorCompra(c) {
  return (c.fase === 'final' && Number(c.valor_final_total) > 0)
    ? Number(c.valor_final_total)
    : Number(c.valor_total || 0)
}
// Prestacoes efectivamente pagas — valor + data (para atribuir ao mes de caixa)
//
// QUANDO O VALOR NAO ESTA GRAVADO, A REPARTICAO SAI DA `sinal_percentagem` E
// NAO DE METADE. Era `base / 2` nas duas, e isso e verdade num acordo 50+50 e
// em mais nenhum:
//
//   Naypes, 100% a cabeca  -> contava-se metade do que saiu mesmo da conta
//   Plus Skill, 20% + 80%  -> contava-se 50% quando tinham saido 20
//
// O rotulo do estado ja tinha sido corrigido para nao mentir sobre a
// percentagem (ver ESTADO_INFO acima). O VALOR nao — e e o valor que entra nas
// contas de caixa, no IVA e no lucro.
function prestacoesPagas(c) {
  const base = valorCompra(c)
  const pct1 = Number(c.sinal_percentagem ?? 50) || 50
  const out = []
  if (c.pago_1a_prestacao) {
    const v = c.pago_1a_valor != null ? Number(c.pago_1a_valor) : base * pct1 / 100
    out.push({ valor: Number(v) || 0, data: c.pago_1a_data || c.data_fatura })
  }
  if (c.pago_2a_prestacao) {
    const v = c.pago_2a_valor != null ? Number(c.pago_2a_valor) : base * (100 - pct1) / 100
    out.push({ valor: Number(v) || 0, data: c.pago_2a_data || c.data_fatura })
  }
  return out
}
function pagoCompra(c) { return prestacoesPagas(c).reduce((s, p) => s + p.valor, 0) }

const mesKey = (d) => d ? String(d).slice(0, 7) : ''
const mesLabel = (key) => {
  const [ano, mes] = String(key).split('-')
  return `${MESES[Number(mes) - 1] || '?'} ${ano}`
}
function csvEscape(v) {
  const s = String(v ?? '')
  return (s.includes(';') || s.includes('"') || s.includes('\n')) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export default function ComprasFornecedor() {
  const { isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [compras, setCompras] = useState([])
  const [fornecedores, setFornecedores] = useState([])
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroSearch, setFiltroSearch] = useState('')
  const [filtroFornecedor, setFiltroFornecedor] = useState('todos')
  const [filtroAno, setFiltroAno] = useState('todos')
  const [filtroMes, setFiltroMes] = useState('todos')
  const [verPorMes, setVerPorMes] = useState(false)
  const [modalNova, setModalNova] = useState(false)
  const [modalDetalhe, setModalDetalhe] = useState(null)
  const [vista, setVista] = useState('facturas')

  const carregar = async () => {
    setLoading(true)
    const [c, f] = await Promise.all([
      supabase.from('compras_fornecedor').select('*, fornecedor:fornecedores(nome)').order('data_fatura', { ascending: false }),
      supabase.from('fornecedores').select('*').eq('ativo', true).order('nome'),
    ])
    setCompras(c.data || [])
    setFornecedores(f.data || [])
    setLoading(false)
  }
  useEffect(() => { if (isAdmin) carregar() }, [isAdmin])

  // Anos disponiveis (a partir da data da factura)
  const anosDisponiveis = useMemo(() => {
    const set = new Set(compras.map(c => mesKey(c.data_fatura).slice(0, 4)).filter(Boolean))
    return Array.from(set).sort((a, b) => b.localeCompare(a))
  }, [compras])

  // Filtros que NAO sao de periodo — base do resumo mensal (para ver sempre todos os meses)
  const filtradasSemPeriodo = useMemo(() => {
    const s = filtroSearch.trim().toLowerCase()
    return compras.filter(c => {
      if (filtroEstado !== 'todos' && c.estado !== filtroEstado) return false
      if (filtroFornecedor !== 'todos' && String(c.fornecedor_id) !== String(filtroFornecedor)) return false
      if (s) {
        if (!(c.numero_fatura || '').toLowerCase().includes(s)
          && !(c.numero_interno || '').toLowerCase().includes(s)
          && !(c.fornecedor?.nome || '').toLowerCase().includes(s)) return false
      }
      return true
    })
  }, [compras, filtroEstado, filtroSearch, filtroFornecedor])

  const filtradas = useMemo(() => {
    return filtradasSemPeriodo.filter(c => {
      const k = mesKey(c.data_fatura)
      if (filtroAno !== 'todos' && k.slice(0, 4) !== filtroAno) return false
      if (filtroMes !== 'todos' && k.slice(5, 7) !== filtroMes) return false
      return true
    })
  }, [filtradasSemPeriodo, filtroAno, filtroMes])

  const temFiltros = filtroEstado !== 'todos' || !!filtroSearch || filtroFornecedor !== 'todos' || filtroAno !== 'todos' || filtroMes !== 'todos'
  const limparFiltros = () => {
    setFiltroEstado('todos'); setFiltroSearch(''); setFiltroFornecedor('todos')
    setFiltroAno('todos'); setFiltroMes('todos')
  }

  // Facturas canceladas nao entram nos totais em euros (salvo se forem o filtro pedido)
  const paraTotais = useMemo(
    () => filtroEstado === 'cancelado' ? filtradas : filtradas.filter(c => c.estado !== 'cancelado'),
    [filtradas, filtroEstado]
  )

  const kpis = useMemo(() => {
    const total = paraTotais.reduce((s, c) => s + valorCompra(c), 0)
    const pago = paraTotais.reduce((s, c) => s + pagoCompra(c), 0)
    const a_pagar = paraTotais.reduce((s, c) => s + Math.max(0, valorCompra(c) - pagoCompra(c)), 0)
    // PENDENTE E QUEM AINDA DEVE DINHEIRO, e nao quem tem um estado por
    // actualizar. Contava-se `estado !== 'pago_total'`, e a factura do Naypes
    // ficava eternamente pendente: paga-se a 100% na primeira prestacao,
    // portanto o estado gravado fica em `pago_1a` e nunca avanca para
    // `pago_total` — quem o avanca assume sempre duas prestacoes.
    //
    // O contador dizia "2 pendentes" com uma so factura por pagar, e a coluna
    // "Em falta" ao lado dizia 0,00 na outra. Dois numeros no mesmo ecra a
    // discordar um do outro, e o certo era o de baixo.
    //
    // A pergunta certa e "falta sair dinheiro desta factura?" — a mesma que a
    // coluna Em falta ja fazia. Agora sao as duas a fazer a mesma pergunta.
    const pendentes = paraTotais.filter(c =>
      c.estado !== 'cancelado' && (valorCompra(c) - pagoCompra(c)) > 0.01).length
    const media = paraTotais.length ? total / paraTotais.length : 0
    return { total, pago, a_pagar, pendentes, nFacturas: paraTotais.length, media }
  }, [paraTotais])

  // Resumo por mes — facturado (data da factura) vs pago (data de cada prestacao)
  const porMes = useMemo(() => {
    const base = filtroEstado === 'cancelado' ? filtradasSemPeriodo : filtradasSemPeriodo.filter(c => c.estado !== 'cancelado')
    const mapa = new Map()
    const get = (k) => {
      if (!mapa.has(k)) mapa.set(k, { key: k, facturas: 0, gasto: 0, pago: 0 })
      return mapa.get(k)
    }
    for (const c of base) {
      const k = mesKey(c.data_fatura)
      if (k) { const m = get(k); m.facturas += 1; m.gasto += valorCompra(c) }
      for (const p of prestacoesPagas(c)) {
        const kp = mesKey(p.data)
        if (kp) get(kp).pago += p.valor
      }
    }
    const linhas = Array.from(mapa.values())
      .map(m => ({ ...m, emFalta: Math.max(0, m.gasto - m.pago) }))
      .sort((a, b) => b.key.localeCompare(a.key))
    const maxGasto = linhas.reduce((mx, m) => Math.max(mx, m.gasto, m.pago), 0)
    const totais = linhas.reduce((t, m) => ({
      facturas: t.facturas + m.facturas, gasto: t.gasto + m.gasto, pago: t.pago + m.pago, emFalta: t.emFalta + m.emFalta,
    }), { facturas: 0, gasto: 0, pago: 0, emFalta: 0 })
    return { linhas, maxGasto, totais }
  }, [filtradasSemPeriodo, filtroEstado])

  const exportarCsvMeses = () => {
    const linhas = [['Mes', 'Facturas', 'Total gasto (EUR)', 'Pago (EUR)', 'Em falta (EUR)'].join(';')]
    for (const m of porMes.linhas) {
      linhas.push([mesLabel(m.key), m.facturas, fmt2(m.gasto), fmt2(m.pago), fmt2(m.emFalta)].map(csvEscape).join(';'))
    }
    linhas.push(['TOTAL', porMes.totais.facturas, fmt2(porMes.totais.gasto), fmt2(porMes.totais.pago), fmt2(porMes.totais.emFalta)].join(';'))
    const blob = new Blob(['﻿' + linhas.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `compras-por-mes-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Clicar numa linha do resumo filtra a tabela por esse mes (ou limpa se ja estiver activo)
  const seleccionarMes = (key) => {
    const ano = key.slice(0, 4), mes = key.slice(5, 7)
    if (filtroAno === ano && filtroMes === mes) { setFiltroAno('todos'); setFiltroMes('todos') }
    else { setFiltroAno(ano); setFiltroMes(mes) }
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: '60px 28px', textAlign: 'center', color: C.danger }}>
        <ShieldOff size={48} style={{ margin: '0 auto 12px' }} />
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Acesso restrito</h2>
        <p style={{ margin: '6px 0 0', fontSize: '13px', color: C.textMuted }}>Esta página só está disponível para administradores.</p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      {/* HEADER */}
      <div style={{ padding: '20px 28px 12px', background: C.surface, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: C.text, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingBag size={22} color={C.primary} /> Compras a Fornecedor
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: C.textMuted }}>
              Facturas de compra, pagamentos em duas prestações e ligação às encomendas que cobrem.
            </p>
          </div>
          <button onClick={() => setModalNova(true)}
            style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Plus size={14} /> Nova Compra
          </button>
        </div>

        {/* SEPARADORES */}
        <div style={{ display: 'flex', gap: '4px', marginTop: '14px', borderBottom: `1px solid ${C.border}` }}>
          {[['facturas', 'Facturas'], ['saldo', 'A pagar vs a receber']].map(([k, label]) => (
            <button key={k} onClick={() => setVista(k)}
              style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${vista === k ? C.primary : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: vista === k ? '800' : '600', color: vista === k ? C.primary : C.textMuted, marginBottom: '-1px' }}>
              {label}
            </button>
          ))}
        </div>

        {vista === 'facturas' && (<>
        {/* KPIs — reflectem sempre os filtros activos */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginTop: '16px' }}>
          <KpiCard label={temFiltros ? 'Total gasto (filtrado)' : 'Total gasto'} valor={fmtEur(kpis.total)} sufixo=" €" cor={C.primary} grande destaque />
          <KpiCard label="Facturas" valor={kpis.nFacturas} sufixo={kpis.pendentes ? ` (${kpis.pendentes} pend.)` : ''} cor={C.text} />
          <KpiCard label="Já pago" valor={fmtEur(kpis.pago)} sufixo=" €" cor={C.success} grande />
          <KpiCard label="Em falta" valor={fmtEur(kpis.a_pagar)} sufixo=" €" cor={C.danger} grande />
          <KpiCard label="Média por factura" valor={fmtEur(kpis.media)} sufixo=" €" cor={C.textMuted} grande />
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '320px' }}>
            <Search size={14} color={C.textMuted} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }} />
            <input value={filtroSearch} onChange={e => setFiltroSearch(e.target.value)}
              placeholder="Procurar nº factura / interno / fornecedor"
              style={{ width: '100%', height: '34px', padding: '0 10px 0 32px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box' }} />
          </div>
          <select value={filtroFornecedor} onChange={e => setFiltroFornecedor(e.target.value)} title="Fornecedor"
            style={{ height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff' }}>
            <option value="todos">Todos os fornecedores</option>
            {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          <select value={filtroAno} onChange={e => setFiltroAno(e.target.value)} title="Ano da factura"
            style={{ height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff' }}>
            <option value="todos">Todos os anos</option>
            {anosDisponiveis.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)} title="Mês da factura"
            style={{ height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff' }}>
            <option value="todos">Todos os meses</option>
            {MESES.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
          </select>
          <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} title="Estado de pagamento"
            style={{ height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '4px', fontSize: '13px', background: '#fff' }}>
            <option value="todos">Todos os estados</option>
            <option value="pendente">Pendente</option>
            <option value="pago_1a">1.ª prestação paga</option>
            <option value="pago_total">100% Pago</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <button onClick={() => setVerPorMes(v => !v)} title="Resumo de gastos por mês"
            style={{ height: '34px', padding: '0 12px', border: `1px solid ${verPorMes ? C.primary : C.border}`, background: verPorMes ? '#eff6fc' : C.surface, color: verPorMes ? C.primary : C.text, borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Calendar size={12} /> Por mês
          </button>
          {temFiltros && (
            <button onClick={limparFiltros} title="Limpar todos os filtros"
              style={{ height: '34px', padding: '0 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', color: C.textMuted }}>
              <X size={12} /> Limpar
            </button>
          )}
          <button onClick={carregar} title="Atualizar"
            style={{ height: '34px', padding: '0 12px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <RefreshCw size={12} /> Atualizar
          </button>
        </div>
        </>)}
      </div>

      {vista === 'saldo' && <SaldoFacturas />}

      {vista === 'facturas' && (<>
      {/* RESUMO POR MÊS */}
      {verPorMes && !loading && (
        <div style={{ padding: '16px 28px 0' }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 12px', borderBottom: `1px solid ${C.border}`, background: C.bg, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: '800', color: C.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={13} color={C.primary} /> Gastos por mês
                <span style={{ fontWeight: '500', color: C.textMuted }}>
                  — facturado pela data da factura, pago pela data de cada prestação. Clica num mês para filtrar.
                </span>
              </span>
              <button onClick={exportarCsvMeses} disabled={porMes.linhas.length === 0}
                style={{ height: '28px', padding: '0 10px', border: `1px solid ${C.border}`, background: C.surface, borderRadius: '4px', cursor: porMes.linhas.length ? 'pointer' : 'not-allowed', fontSize: '11px', fontWeight: '700', color: C.textMuted }}>
                Exportar CSV
              </button>
            </div>
            {porMes.linhas.length === 0 ? (
              <p style={{ margin: 0, padding: '20px', textAlign: 'center', fontSize: '13px', color: C.textMuted }}>Sem dados para os filtros actuais.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Mês</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Facturas</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Total gasto</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Pago</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Em falta</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', width: '22%' }}>Peso</th>
                  </tr>
                </thead>
                <tbody>
                  {porMes.linhas.map(m => {
                    const activo = filtroAno === m.key.slice(0, 4) && filtroMes === m.key.slice(5, 7)
                    const pct = porMes.maxGasto > 0 ? (m.gasto / porMes.maxGasto) * 100 : 0
                    return (
                      <tr key={m.key} onClick={() => seleccionarMes(m.key)}
                        style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: activo ? '#eff6fc' : 'transparent' }}
                        onMouseEnter={e => { if (!activo) e.currentTarget.style.background = '#fafafa' }}
                        onMouseLeave={e => { if (!activo) e.currentTarget.style.background = 'transparent' }}>
                        <td style={{ padding: '8px 12px', fontWeight: '700', color: activo ? C.primary : C.text }}>{mesLabel(m.key)}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: C.textMuted }}>{m.facturas || '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '800', color: C.text }}>{fmtEur(m.gasto)} €</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: C.success, fontWeight: '600' }}>{fmtEur(m.pago)} €</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', color: m.emFalta > 0 ? C.danger : C.textMuted, fontWeight: m.emFalta > 0 ? '700' : '400' }}>{fmtEur(m.emFalta)} €</td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ height: '8px', background: C.bg, borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: C.primary, borderRadius: '4px' }} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: C.bg, borderTop: `2px solid ${C.border}` }}>
                    <td style={{ padding: '10px 12px', fontWeight: '800', color: C.text }}>TOTAL ({porMes.linhas.length} {porMes.linhas.length === 1 ? 'mês' : 'meses'})</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', color: C.text }}>{porMes.totais.facturas}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', color: C.primary, fontSize: '14px' }}>{fmtEur(porMes.totais.gasto)} €</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', color: C.success }}>{fmtEur(porMes.totais.pago)} €</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', color: porMes.totais.emFalta > 0 ? C.danger : C.textMuted }}>{fmtEur(porMes.totais.emFalta)} €</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* TABELA */}
      <div style={{ padding: '16px 28px', flex: 1 }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: C.textMuted, fontSize: '13px', padding: '40px' }}>A carregar...</p>
        ) : filtradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textMuted }}>
            <FileText size={48} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '14px' }}>{compras.length === 0 ? 'Ainda não há compras registadas.' : 'Nenhuma compra corresponde aos filtros.'}</p>
            {compras.length === 0 && (
              <button onClick={() => setModalNova(true)} style={{ marginTop: '16px', background: C.primary, color: '#fff', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                Criar primeira compra
              </button>
            )}
          </div>
        ) : (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Nº Interno</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Nº Factura</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Fornecedor</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Data</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Valor</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }} title="Valor da próxima prestação a pagar ao fornecedor. A percentagem da 1ª é definida por compra: 100% nos fornecedores de pagamento total, 20% no acordo do sinal reduzido, 50% nos restantes.">Em falta</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>1ª</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>2ª</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((c, i) => {
                  const est = ESTADO_INFO[c.estado] || ESTADO_INFO.pendente
                  return (
                    <tr key={c.id}
                      onClick={() => setModalDetalhe(c)}
                      style={{ borderBottom: i < filtradas.length - 1 ? `1px solid ${C.border}` : 'none', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                      onMouseLeave={e => e.currentTarget.style.background = C.surface}>
                      <td style={{ padding: '10px 12px', fontWeight: '700', color: C.primary, fontFamily: 'monospace' }}>{c.numero_interno || '—'}</td>
                      <td style={{ padding: '10px 12px', color: C.text, fontWeight: '600' }}>{c.numero_fatura}</td>
                      <td style={{ padding: '10px 12px', color: C.text }}>{c.fornecedor?.nome || '—'}</td>
                      <td style={{ padding: '10px 12px', color: C.textMuted }}>{fmtDate(c.data_fatura)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', color: C.text }}>
                        {(() => {
                          const ehFinal = c.fase === 'final' && Number(c.valor_final_total) > 0
                          const total = ehFinal ? Number(c.valor_final_total) : Number(c.valor_total || 0)
                          return (
                            <span title={ehFinal ? `Valor final (previsão: ${fmt2(c.valor_total)} €)` : 'Valor previsão'} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {fmt2(total)} €
                              {ehFinal && (
                                <span style={{ padding: '1px 5px', fontSize: '9px', fontWeight: '800', background: C.success, color: '#fff', borderRadius: '3px' }}>F</span>
                              )}
                            </span>
                          )
                        })()}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {(() => {
                          const ehFinal = c.fase === 'final' && Number(c.valor_final_total) > 0
                          const total = ehFinal ? Number(c.valor_final_total) : Number(c.valor_total || 0)
                          // Percentagem da 1.ª ao fornecedor (50 histórico, 20 no
                          // acordo Plus Skill). A 2.ª é sempre o resto.
                          const pctForn = Number(c.sinal_percentagem ?? 50) || 50
                          const pago1 = !!c.pago_1a_prestacao
                          const pago2 = !!c.pago_2a_prestacao
                          if (pago1 && pago2) return <span style={{ color: C.textMuted, fontSize: '11px' }}>pago</span>
                          const primeira = Math.round((total * pctForn / 100) * 100) / 100
                          const emFalta = pago1
                            ? Math.max(0, Math.round((total - Number(c.pago_1a_valor || primeira)) * 100) / 100)
                            : primeira
                          const cor = pago1 ? C.primary : '#c07400'
                          const tip = pago1
                            ? `2ª prestação em falta (${Math.round((100 - pctForn) * 10) / 10}%)`
                            : `1ª prestação em falta (${pctForn}%)`
                          return <span style={{ color: cor, fontWeight: '700' }} title={tip}>{fmt2(emFalta)} €</span>
                        })()}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{c.pago_1a_prestacao ? <CheckCircle size={14} color={C.success} /> : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>{c.pago_2a_prestacao ? <CheckCircle size={14} color={C.success} /> : '—'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {(() => {
                          // Com a 1.ª paga, mostra-se a percentagem DESTA compra
                          // (20% no acordo, 50% nas normais) em vez de um texto
                          // fixo. O `est.label` fica para os outros estados.
                          const pctPago = Number(c.sinal_percentagem ?? 50) || 50
                          const rotulo = c.estado === 'pago_1a'
                            ? `${Math.round(pctPago * 10) / 10}% Pago`
                            : est.label
                          return (
                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: est.bg, color: est.cor }}
                                  title={c.estado === 'pago_1a' ? `1.ª prestação (${pctPago}%) paga — falta a 2.ª` : undefined}>
                              {rotulo}
                            </span>
                          )
                        })()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: C.bg, borderTop: `2px solid ${C.border}` }}>
                  <td colSpan={4} style={{ padding: '10px 12px', fontWeight: '800', color: C.text }}>
                    TOTAL — {kpis.nFacturas} {kpis.nFacturas === 1 ? 'factura' : 'facturas'}
                    {filtradas.length !== kpis.nFacturas && (
                      <span style={{ fontWeight: '500', color: C.textMuted, fontSize: '11px' }}> (canceladas excluídas)</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '800', color: C.primary, fontSize: '14px' }}>{fmtEur(kpis.total)} €</td>
                  <td colSpan={4} style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', color: C.textMuted }}>
                    Pago <b style={{ color: C.success }}>{fmtEur(kpis.pago)} €</b>
                    {' · '}Em falta <b style={{ color: kpis.a_pagar > 0 ? C.danger : C.textMuted }}>{fmtEur(kpis.a_pagar)} €</b>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      </>)}

      {/* MODAL NOVA COMPRA */}
      {modalNova && (
        <ModalNovaCompra
          fornecedores={fornecedores}
          onClose={() => setModalNova(false)}
          onCreated={async (nova) => {
            await carregar()
            setModalNova(false)
            // Abrir detalhe automaticamente para o admin linkar encomendas
            if (nova?.id) {
              const { data: fresca } = await supabase.from('compras_fornecedor')
                .select('*, fornecedor:fornecedores(nome)').eq('id', nova.id).single()
              if (fresca) setModalDetalhe(fresca)
            }
          }}
        />
      )}

      {/* MODAL DETALHE */}
      {modalDetalhe && (
        <ModalDetalheCompra
          compra={modalDetalhe}
          fornecedores={fornecedores}
          onClose={() => setModalDetalhe(null)}
          onUpdated={async () => { await carregar() }}
          onDeleted={async () => { await carregar() }}
        />
      )}
    </div>
  )
}

function KpiCard({ label, valor, sufixo, cor, grande, destaque }) {
  return (
    <div style={{
      background: destaque ? '#eff6fc' : C.surface,
      border: `1px solid ${destaque ? C.primary : C.border}`,
      borderRadius: '4px', padding: '12px 14px',
    }}>
      <p style={{ margin: 0, fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
      <p style={{ margin: '4px 0 0', fontSize: grande ? '20px' : '22px', fontWeight: '800', color: cor }}>{valor}{sufixo}</p>
    </div>
  )
}

// ─── Modal Nova Compra ─────────────────────────────────────────────────────
function ModalNovaCompra({ fornecedores, onClose, onCreated }) {
  const [form, setForm] = useState({
    fornecedor_id: fornecedores[0]?.id || '',
    numero_fatura: '',
    data_fatura: new Date().toISOString().slice(0, 10),
    valor_sem_iva: '',
    valor_iva: '0',           // default 0 (intra-comunitario OSS)
    valor_total: '',
    com_iva: false,            // checkbox: factura inclui IVA?
    notas_admin: '',
  })
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  // Auto-fill por PDF
  const [pdfFile, setPdfFile] = useState(null)
  const [parsing, setParsing] = useState(false)
  const [parsed, setParsed] = useState(null)    // { numero_fatura, data_fatura, valor_total, linhas, ok, avisos }
  // % da 1.ª prestação ao fornecedor. 50 é o normal; 20 quando a factura só
  // tem artigos do acordo. Proposto pela detecção, confirmado por quem importa.
  const [pctForn, setPctForn] = useState(50)
  const [pctFornTocado, setPctFornTocado] = useState(false)

  // Tabela de produtos_internos para validar valores do PDF vs BD
  const [produtosBD, setProdutosBD] = useState([]) // [{ nome, ean, preco_custo, sinal_flexivel }]
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      // sinal_flexivel vem junto para detectar, na importação, se a factura é
      // do acordo — ver `validacao.todosDoAcordo`.
      // Paginado (src/lerTudo.js): sem isto o mapa Nome->EAN ficava sem os
      // artigos a partir da milesima linha e a factura importada nao os
      // reconhecia — apareciam como produtos novos.
      const { data: prod } = await lerTudo(() => supabase.from('Produtos').select('id, Nome, EAN, sinal_flexivel'), 'id')
      const { data: int } = await lerTudo(() => supabase.from('produtos_internos').select('produto_nome, preco_custo'), 'produto_nome')
      if (cancelado) return
      const eanPorNome = new Map((prod || []).map(p => [p.Nome, p.EAN ? String(p.EAN) : null]))
      const flexPorNome = new Map((prod || []).map(p => [p.Nome, p.sinal_flexivel === true]))
      const lista = (int || []).map(r => ({
        nome: r.produto_nome,
        ean: eanPorNome.get(r.produto_nome) || null,
        preco_custo: r.preco_custo != null ? Number(r.preco_custo) : null,
        sinal_flexivel: flexPorNome.get(r.produto_nome) === true,
      }))
      setProdutosBD(lista)
    })()
    return () => { cancelado = true }
  }, [])

  // Validacao do PDF contra BD: por cada linha do PDF, compara custo_unit_real com preco_custo
  const validacao = useMemo(() => {
    if (!parsed || !parsed.linhas || !produtosBD.length) return null
    const porEan = new Map(produtosBD.filter(p => p.ean).map(p => [String(p.ean), p]))
    const porNome = new Map(produtosBD.map(p => [norm(p.nome), p]))
    let totalEsperado = 0
    let totalPdf = 0
    let temErro = false
    let temAviso = false
    const linhas = parsed.linhas.map(l => {
      const eanStr = l.ean ? String(l.ean) : ''
      const match = (eanStr && porEan.get(eanStr)) || porNome.get(norm(l.nome || '')) || null
      const precoBD = match?.preco_custo ?? null
      const precoPdf = Number(l.custo_unit_real || 0)
      const valLinhaPdf = Number(l.valor_total || 0)
      totalPdf += valLinhaPdf
      let estado = 'ok'
      let diff = 0
      if (precoBD == null) {
        estado = 'sem_custo'
      } else {
        diff = precoPdf - precoBD
        const diffPct = precoBD > 0 ? Math.abs(diff) / precoBD : 0
        if (Math.abs(diff) < 0.01) estado = 'ok'
        else if (diffPct <= 0.05) { estado = 'aviso'; temAviso = true }
        else { estado = 'erro'; temErro = true }
        totalEsperado += precoBD * Number(l.qty || 0)
      }
      return { ...l, preco_bd: precoBD, preco_pdf: precoPdf, diff, diff_pct: precoBD ? diff / precoBD : 0, estado, sinal_flexivel: match?.sinal_flexivel === true }
    })
    const semCusto = linhas.filter(l => l.estado === 'sem_custo').length
    const erros = linhas.filter(l => l.estado === 'erro').length
    const avisos = linhas.filter(l => l.estado === 'aviso').length
    const oks = linhas.filter(l => l.estado === 'ok').length
    const totalPdfReal = Number(parsed.valor_total || totalPdf)
    const diffTotal = totalPdfReal - totalEsperado
    // Detecção do acordo: a factura é do sinal reduzido se TODAS as linhas
    // reconhecidas forem de produtos marcados. Mesma regra do carrinho — basta
    // um artigo de fora para não ser. Linhas que não deram match não contam:
    // se nem sequer sabemos que produto é, não podemos afirmar que está no acordo.
    const reconhecidas = linhas.filter(l => l.estado !== 'sem_custo')
    const doAcordo = reconhecidas.filter(l => l.sinal_flexivel === true)
    const todosDoAcordo = reconhecidas.length > 0 && doAcordo.length === reconhecidas.length
    // A 1.ª prestação mostrada sai de `pctForn`, que o utilizador pode ajustar —
    // não daqui. Por isso não se devolve valor sugerido nenhum.
    return { linhas, totalPdf: totalPdfReal, totalEsperado, diffTotal, semCusto, erros, avisos, oks, temErro, temAviso, todosDoAcordo, nDoAcordo: doAcordo.length, nReconhecidas: reconhecidas.length }
  }, [parsed, produtosBD])

  // Há fornecedores que só vendem contra pagamento total. Nesses não existe
  // 1.ª e 2.ª prestação nenhuma: paga-se tudo, e é por isso que também exigimos
  // 100% ao cliente. O formulário entrava sempre a 50% e obrigava a corrigir à
  // mão em cada factura — e uma percentagem esquecida deixa metade da factura
  // eternamente "por pagar" no saldo ao fornecedor.
  const fornDaCompra = fornecedores.find(f => String(f.id) === String(form.fornecedor_id))
  const fornPagaTudo = !!fornDaCompra?.exige_pagamento_total

  // Aplica a detecção — mas nunca por cima do que o utilizador já escreveu.
  useEffect(() => {
    if (pctFornTocado) return
    if (fornPagaTudo) { setPctForn(100); return }
    if (!validacao) return
    setPctForn(validacao.todosDoAcordo ? 20 : 50)
  }, [validacao?.todosDoAcordo, pctFornTocado, fornPagaTudo])

  const handlePdfUpload = async (file) => {
    if (!file) return
    setPdfFile(file)
    setParsing(true)
    setErro('')
    try {
      const result = await parseFaturaPdfFile(file)
      setParsed(result)
      if (result.ok) {
        // O leitor sabe de quem e a factura. Escolher o fornecedor a partir
        // dela evita o caso em que fica seleccionado o que estava por omissao —
        // e a factura de um vai parar a compra do outro.
        const fornDetectado = result.fornecedor
          ? fornecedores.find(f => (f.nome || '').toLowerCase().includes(String(result.fornecedor).toLowerCase()))
          : null
        // Pre-encher form com dados detectados
        setForm(p => ({
          ...p,
          fornecedor_id: fornDetectado?.id || p.fornecedor_id,
          numero_fatura: result.numero_fatura || p.numero_fatura,
          data_fatura: result.data_fatura || p.data_fatura,
          valor_sem_iva: String(result.valor_sem_iva.toFixed(2)),
          valor_iva: '0',
          valor_total: String(result.valor_total.toFixed(2)),
          com_iva: false,
        }))
      } else {
        setErro('Parser nao conseguiu extrair tudo. Verifica os avisos e preenche o que faltar manualmente.')
      }
    } catch (e) {
      setErro('Erro ao ler PDF: ' + (e?.message || String(e)))
      setParsed(null)
    } finally {
      setParsing(false)
    }
  }

  const onValorChange = (campo, valor) => {
    const v = valor.replace(',', '.').replace(/[^0-9.]/g, '')
    setForm(p => {
      const next = { ...p, [campo]: v }
      const semIva = Number(v) || 0
      if (campo === 'valor_sem_iva') {
        if (p.com_iva) {
          next.valor_iva = (semIva * 0.23).toFixed(2)
          next.valor_total = (semIva * 1.23).toFixed(2)
        } else {
          next.valor_iva = '0'
          next.valor_total = v
        }
      } else if (campo === 'valor_total') {
        if (p.com_iva) {
          next.valor_sem_iva = (semIva / 1.23).toFixed(2)
          next.valor_iva = (semIva - semIva / 1.23).toFixed(2)
        } else {
          next.valor_sem_iva = v
          next.valor_iva = '0'
        }
      }
      return next
    })
  }
  const toggleIva = (com) => {
    setForm(p => {
      const semIva = Number(p.valor_sem_iva) || 0
      if (com) {
        return { ...p, com_iva: true, valor_iva: (semIva * 0.23).toFixed(2), valor_total: (semIva * 1.23).toFixed(2) }
      } else {
        const total = Number(p.valor_total) || semIva
        return { ...p, com_iva: false, valor_sem_iva: String(total), valor_iva: '0', valor_total: String(total) }
      }
    })
  }

  const submit = async () => {
    if (!form.fornecedor_id) { setErro('Escolhe um fornecedor.'); return }
    if (!form.numero_fatura.trim()) { setErro('Indica o nº de factura.'); return }
    if (!form.data_fatura) { setErro('Indica a data.'); return }
    if (!form.valor_total || Number(form.valor_total) <= 0) { setErro('Valor total tem de ser > 0.'); return }
    setErro(''); setSaving(true)
    const forn = fornecedores.find(f => f.id === form.fornecedor_id)
    const prazo = Number(forn?.prazo_envio_dias || 25)
    const dataEnvio = new Date(form.data_fatura); dataEnvio.setDate(dataEnvio.getDate() + prazo)
    const numFatura = form.numero_fatura.trim()
    const valorTotalSemIva = Number(form.valor_sem_iva) || 0
    const { data: novaCompra, error } = await supabase.from('compras_fornecedor').insert({
      fornecedor_id: form.fornecedor_id,
      numero_fatura: numFatura,
      data_fatura: form.data_fatura,
      data_envio_prevista: dataEnvio.toISOString().slice(0, 10),
      valor_sem_iva: valorTotalSemIva,
      valor_iva: Number(form.valor_iva) || 0,
      valor_total: Number(form.valor_total) || 0,
      // Detectada na importação (todas as linhas do acordo => 20%), ajustável
      // pelo utilizador antes de gravar e depois na ficha da compra.
      sinal_percentagem: pctForn,
      notas_admin: form.notas_admin.trim() || null,
    }).select().single()
    if (error) { setSaving(false); setErro('Erro: ' + error.message); return }

    // Upload do PDF se foi anexado (auto-faz parte do fluxo)
    if (pdfFile) {
      try {
        const ext = (pdfFile.name.split('.').pop() || 'pdf').toLowerCase()
        const path = `${novaCompra.id}/fatura.${ext}`
        const { error: upErr } = await supabase.storage.from('faturas-fornecedor').upload(path, pdfFile, { upsert: true, contentType: pdfFile.type })
        if (!upErr) {
          await supabase.from('compras_fornecedor').update({ fatura_pdf_url: path, fatura_pdf_em: new Date().toISOString() }).eq('id', novaCompra.id)
        }
      } catch (e) { console.warn('Upload PDF falhou:', e) }
    }

    // AUTO-LINK por nº factura — 2 queries (mais robusto que join filter)
    try {
      // 1) Encomendas com este fornecedor_fatura_numero
      const { data: encs, error: encsErr } = await supabase
        .from('encomendas')
        .select('id')
        .eq('fornecedor_fatura_numero', numFatura)
        .neq('estado', 'cancelado')
      if (encsErr) throw new Error('Encomendas: ' + encsErr.message)
      const encIds = (encs || []).map(e => e.id)
      let itensMatch = []
      if (encIds.length > 0) {
        const { data, error: itensErr } = await supabase
          .from('encomenda_itens')
          .select('encomenda_id, produto_id, produto_nome, produto_ean, quantidade')
          .in('encomenda_id', encIds)
        if (itensErr) throw new Error('Itens: ' + itensErr.message)
        itensMatch = data || []
      }
      if (itensMatch && itensMatch.length > 0) {
        // Estrategia 1 (preferida): se temos linhas parsed, fazer match por EAN
        // Estrategia 2 (fallback): preco_custo do catalogo (produtos_internos) por produto
        // NUNCA usar custo medio uniforme — distorce margem por produto
        const custoPorEan = new Map()
        if (parsed && parsed.ok && parsed.linhas?.length) {
          for (const ln of parsed.linhas) {
            if (ln.ean && ln.custo_unit_real > 0) {
              custoPorEan.set(String(ln.ean).trim(), Number(ln.custo_unit_real))
            }
          }
        }
        const nomesUnicos = [...new Set(itensMatch.map(it => it.produto_nome).filter(Boolean))]
        const { data: catCustos } = await supabase
          .from('produtos_internos')
          .select('produto_nome, preco_custo')
          .in('produto_nome', nomesUnicos)
        const custoPorNome = new Map((catCustos || []).map(r => [r.produto_nome, Number(r.preco_custo)]))

        const semCustoAuto = new Set()
        const linkRows = itensMatch.map(it => {
          const ean = it.produto_ean ? String(it.produto_ean).trim() : null
          let custoUn = null
          if (ean && custoPorEan.has(ean)) custoUn = custoPorEan.get(ean)
          else if (custoPorNome.has(it.produto_nome)) custoUn = custoPorNome.get(it.produto_nome)
          const custoUnNum = custoUn != null ? Number(Number(custoUn).toFixed(4)) : null
          const valorAloc = custoUnNum != null ? Number((custoUnNum * Number(it.quantidade)).toFixed(2)) : null
          if (!(custoUnNum > 0)) semCustoAuto.add(it.produto_nome)
          return {
            compra_id: novaCompra.id,
            encomenda_id: it.encomenda_id,
            produto_id: it.produto_id,
            produto_nome: it.produto_nome,
            quantidade_alocada: it.quantidade,
            custo_unitario_previsao: custoUnNum,
            custo_unitario: custoUnNum,
            valor_alocado_previsao: valorAloc,
            valor_alocado: valorAloc,
          }
        })
        const { error: linkErr } = await supabase.from('compras_fornecedor_encomendas').insert(linkRows)
        if (linkErr) throw new Error('Inserir links: ' + linkErr.message)
        // Ligação automática ao criar a compra: não dá para perguntar antes, mas o
        // admin tem de saber que estas linhas ficaram sem custo — senão o lucro
        // desta factura aparece inflacionado em toda a app, sem aviso nenhum.
        if (semCustoAuto.size > 0) {
          const nomes = [...semCustoAuto]
          alert(
            'Compra criada e encomendas ligadas.\n\n' +
            `ATENÇÃO: ${nomes.length} produto(s) ficaram SEM CUSTO por não terem preço de custo registado:\n\n· ` +
            nomes.slice(0, 8).join('\n· ') +
            (nomes.length > 8 ? `\n… e mais ${nomes.length - 8}` : '') +
            '\n\nEnquanto assim estiver, o lucro desta factura aparece inflacionado. ' +
            'Preenche o preço de custo destes produtos e corrige os custos no detalhe da compra.'
          )
        }

        // Tambem actualizar fornecedor_fatura_numero nas encomendas (caso ainda nao esteja)
        const encIdsUnique = [...new Set(itensMatch.map(it => it.encomenda_id))]
        await supabase.from('encomendas').update({ fornecedor_fatura_numero: numFatura }).in('id', encIdsUnique)
      }
    } catch (e) {
      console.warn('Auto-link falhou:', e)
      alert('Compra criada, mas auto-link falhou:\n' + (e?.message || String(e)) + '\n\nUsa o botao "Auto-linkar" no detalhe.')
    }

    setSaving(false)
    await onCreated(novaCompra)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.surface, borderRadius: '6px', maxWidth: '480px', width: '100%', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: C.text }}>Nova Compra a Fornecedor</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' }}>
          {erro && <div style={{ padding: '8px 12px', background: '#fde7e9', border: `1px solid ${C.danger}`, borderRadius: '3px', fontSize: '12px', color: C.danger }}>{erro}</div>}

          {/* ZONA DE UPLOAD PDF — auto-preenche tudo */}
          <div style={{ padding: '12px', background: parsed?.ok ? '#e8f5e9' : '#eff6fc', border: `2px dashed ${parsed?.ok ? C.success : C.primary}`, borderRadius: '4px' }}>
            <p style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: '700', color: parsed?.ok ? C.success : C.primary, display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={13} /> AUTO-PREENCHIMENTO POR PDF
            </p>
            {!pdfFile ? (
              <>
                {/* Sem nome de fornecedor: dizia "PDF da factura PLUS SKILL"
                    mesmo com outro escolhido em cima. E o leitor so conhece,
                    para ja, o formato de um deles — por isso promete-se
                    tentativa, nao resultado, e diz-se o que fazer se falhar. */}
                <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.text }}>
                  Arrasta ou escolhe o PDF da factura para tentar auto-preencher nº, data, valor e linhas com EANs + custos reais.
                  <br />
                  <span style={{ color: C.textMuted }}>
                    O leitor ainda só reconhece o formato de alguns fornecedores. Se não conseguir ler, preenche à mão em baixo — não se perde nada.
                  </span>
                </p>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: C.primary, color: '#fff', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: '700' }}>
                  <FileSearch size={13} /> Carregar PDF da factura
                  <input type="file" accept="application/pdf" style={{ display: 'none' }}
                    onChange={(ev) => { const f = ev.target.files?.[0]; if (f) handlePdfUpload(f); ev.target.value = '' }} />
                </label>
                <p style={{ margin: '6px 0 0', fontSize: '10px', color: C.textMuted }}>
                  Ou preenche o form manualmente em baixo.
                </p>
              </>
            ) : parsing ? (
              <p style={{ margin: 0, fontSize: '12px', color: C.primary }}>⏳ A analisar PDF...</p>
            ) : parsed ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <CheckCircle size={14} color={parsed.ok ? C.success : C.warning} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: parsed.ok ? C.success : C.warning }}>
                    {parsed.ok ? 'PDF analisado com sucesso' : 'Análise parcial — verifica avisos'}
                  </span>
                  <button onClick={() => { setPdfFile(null); setParsed(null) }}
                    style={{ marginLeft: 'auto', background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: '11px' }}>
                    Trocar PDF
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '6px', fontSize: '11px', marginBottom: '6px' }}>
                  <div><strong>Nº:</strong> {parsed.numero_fatura || '—'}</div>
                  <div><strong>Data:</strong> {parsed.data_fatura || '—'}</div>
                  <div><strong>Total:</strong> {parsed.valor_total > 0 ? `${parsed.valor_total.toFixed(2)} €` : '—'}</div>
                  <div><strong>Linhas:</strong> {parsed.linhas?.length || 0}</div>
                </div>
                {parsed.linhas?.length > 0 && validacao && (
                  <div style={{ marginTop: '8px', padding: '10px 12px', background: validacao.temErro ? '#fde7e9' : validacao.temAviso ? '#fff4ce' : '#dff6dd', border: `1px solid ${validacao.temErro ? C.danger : validacao.temAviso ? C.warning : C.success}55`, borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: validacao.temErro ? C.danger : validacao.temAviso ? '#92400e' : '#107c10', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {validacao.temErro ? <AlertTriangle size={14} /> : validacao.temAviso ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
                      Validação contra preços de custo na BD: {validacao.oks} ✓ OK · {validacao.avisos} ⚠ Pequena diff · {validacao.erros} 🚫 Grande diff · {validacao.semCusto} ❓ Sem custo BD
                    </p>
                    <details open={validacao.temErro || validacao.temAviso || validacao.semCusto > 0}>
                      <summary style={{ fontSize: '11px', color: C.primary, cursor: 'pointer', fontWeight: '600' }}>Ver {parsed.linhas.length} linhas comparadas</summary>
                      <div style={{ maxHeight: '220px', overflowY: 'auto', marginTop: '6px', border: `1px solid ${C.border}`, borderRadius: '3px', background: '#fff' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                          <thead>
                            <tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                              <th style={{ padding: '4px 6px', textAlign: 'left' }}>EAN</th>
                              <th style={{ padding: '4px 6px', textAlign: 'left' }}>Produto</th>
                              <th style={{ padding: '4px 6px', textAlign: 'center' }}>Qty</th>
                              <th style={{ padding: '4px 6px', textAlign: 'right' }}>€/un PDF</th>
                              <th style={{ padding: '4px 6px', textAlign: 'right' }}>€/un BD</th>
                              <th style={{ padding: '4px 6px', textAlign: 'right' }}>Diff</th>
                              <th style={{ padding: '4px 6px', textAlign: 'center' }}>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {validacao.linhas.map(l => {
                              const bgRow = l.estado === 'erro' ? '#fde7e955' : l.estado === 'aviso' ? '#fff4ce55' : l.estado === 'sem_custo' ? '#f3f0ff55' : 'transparent'
                              const corEstado = l.estado === 'erro' ? C.danger : l.estado === 'aviso' ? '#c07400' : l.estado === 'sem_custo' ? '#6264a7' : C.success
                              const labelEstado = l.estado === 'erro' ? '🚫' : l.estado === 'aviso' ? '⚠' : l.estado === 'sem_custo' ? '❓' : '✓'
                              return (
                                <tr key={l.idx} style={{ borderTop: `1px solid ${C.border}`, background: bgRow }}>
                                  <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>{l.ean || '?'}</td>
                                  <td style={{ padding: '4px 6px' }}>{(l.nome || '—').slice(0, 36)}{(l.nome?.length || 0) > 36 ? '…' : ''}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'center' }}>{l.qty}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: '700' }}>{l.preco_pdf.toFixed(2)}€</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right', color: l.preco_bd == null ? C.textMuted : C.text }}>{l.preco_bd == null ? '—' : l.preco_bd.toFixed(2) + '€'}</td>
                                  <td style={{ padding: '4px 6px', textAlign: 'right', color: corEstado, fontWeight: '700' }}>
                                    {l.preco_bd == null ? '—' : (l.diff > 0 ? '+' : '') + l.diff.toFixed(2) + '€'}
                                  </td>
                                  <td style={{ padding: '4px 6px', textAlign: 'center', color: corEstado, fontWeight: '700' }}>{labelEstado}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </details>
                    <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px', fontSize: '11px' }}>
                      <div style={{ background: '#fff', padding: '6px 8px', borderRadius: '3px', border: `1px solid ${C.border}` }}>
                        <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Total PDF</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>{validacao.totalPdf.toFixed(2)} €</div>
                      </div>
                      <div style={{ background: '#fff', padding: '6px 8px', borderRadius: '3px', border: `1px solid ${C.border}` }}>
                        <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Total esperado (BD)</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: C.text }}>{validacao.totalEsperado.toFixed(2)} €</div>
                      </div>
                      <div style={{ background: '#fff', padding: '6px 8px', borderRadius: '3px', border: `1px solid ${Math.abs(validacao.diffTotal) > 50 ? C.danger : Math.abs(validacao.diffTotal) > 0.5 ? C.warning : C.success}` }}>
                        <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Diferença</div>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: Math.abs(validacao.diffTotal) > 50 ? C.danger : Math.abs(validacao.diffTotal) > 0.5 ? '#c07400' : '#107c10' }}>
                          {validacao.diffTotal > 0 ? '+' : ''}{validacao.diffTotal.toFixed(2)} €
                        </div>
                      </div>
                      <div style={{ background: '#0a0a0a', padding: '6px 8px', borderRadius: '3px', color: '#fff' }}>
                        <div style={{ color: '#FACC15', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>1.ª Prestação ({pctForn}%)</div>
                        <div style={{ fontSize: '13px', fontWeight: '700' }}>{(validacao.totalPdf * pctForn / 100).toFixed(2)} €</div>
                      </div>
                    </div>

                    {/* Detecção do acordo do sinal reduzido. Proposta, não imposição:
                        a factura do fornecedor é que manda, isto é só a calculadora. */}
                    <div style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '3px', background: validacao.todosDoAcordo ? '#fff8e1' : C.surface, border: `1px solid ${validacao.todosDoAcordo ? `${C.warning}66` : C.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '11px', color: validacao.todosDoAcordo ? '#92400e' : C.textMuted, lineHeight: 1.45, flex: 1, minWidth: '200px' }}>
                          {validacao.todosDoAcordo
                            ? <><strong>Factura do acordo detectada.</strong> Os {validacao.nReconhecidas} artigos reconhecidos estão todos marcados com sinal flexível — 1.ª prestação a <strong>20%</strong>.</>
                            : validacao.nDoAcordo > 0
                              ? <><strong>Factura mista.</strong> Só {validacao.nDoAcordo} de {validacao.nReconhecidas} artigos estão no acordo — fica nos <strong>50%</strong> normais. Ajusta se o fornecedor disser outra coisa.</>
                              : <>Nenhum artigo no acordo do sinal reduzido — condições normais de <strong>50%</strong>.</>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <input type="number" step="1" min="1" max="100" value={pctForn}
                            onChange={e => { setPctFornTocado(true); setPctForn(Number(e.target.value) || 0) }}
                            style={{ width: '58px', height: '28px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', fontWeight: '700', textAlign: 'right', outline: 'none' }} />
                          <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted }}>%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {parsed.avisos?.length > 0 && (
                  <div style={{ marginTop: '6px', padding: '6px 8px', background: '#fff4ce', border: `1px solid ${C.warning}55`, borderRadius: '3px', fontSize: '11px', color: C.warning }}>
                    {parsed.avisos.map((a, i) => <div key={i}>⚠️ {a}</div>)}
                  </div>
                )}
                {/* Debug: ver texto extraido (util quando parser falha) */}
                {parsed.raw_text && (
                  <details style={{ marginTop: '6px' }}>
                    <summary style={{ fontSize: '11px', color: C.textMuted, cursor: 'pointer' }}>🔍 Ver/copiar texto extraído (debug)</summary>
                    <div style={{ marginTop: '6px' }}>
                      <button onClick={() => { navigator.clipboard.writeText(parsed.raw_text); alert('Texto copiado!') }}
                        style={{ background: C.primary, color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', marginBottom: '6px' }}>
                        📋 Copiar texto extraído
                      </button>
                      <textarea readOnly value={parsed.raw_text} rows={10}
                        style={{ width: '100%', fontFamily: 'monospace', fontSize: '10px', padding: '6px', border: `1px solid ${C.border}`, borderRadius: '3px', background: '#fafafa', resize: 'vertical' }} />
                    </div>
                  </details>
                )}
                <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.text }}>
                  💡 Custos reais por EAN serão usados ao linkar encomendas (em vez de custo médio).
                </p>
              </>
            ) : null}
          </div>

          {/* Aviso campos obrigatorios vs auto */}
          <div style={{ padding: '8px 12px', background: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '3px', fontSize: '11px', color: C.text }}>
            <strong>Manual:</strong> nº factura, data, valor.{' '}
            {/* O prazo sai da ficha do fornecedor escolhido, não é fixo. Dizia
                "+25 dias" — o da Plus Skill — e numa compra do Naypes, que
                entrega em 2, prometia três semanas a mais do que a realidade. */}
            <strong>Auto:</strong> nº interno (COMP/2026/...), data de envio prevista
            {fornDaCompra?.prazo_envio_dias ? ` (+${fornDaCompra.prazo_envio_dias} dias, da ficha do fornecedor)` : ' (prazo da ficha do fornecedor)'},
            {' '}estado (pendente), fase (previsão).
          </div>

          <Campo label="Fornecedor *">
            <select value={form.fornecedor_id} onChange={e => setForm(p => ({ ...p, fornecedor_id: e.target.value }))}
              style={inputStyle()}>
              {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </Campo>
          <Campo label="Nº de factura do fornecedor *">
            <input value={form.numero_fatura} onChange={e => setForm(p => ({ ...p, numero_fatura: e.target.value }))}
              placeholder="ex: 68" style={inputStyle()} />
          </Campo>
          <Campo label="Data da factura *">
            <input type="date" value={form.data_fatura} onChange={e => setForm(p => ({ ...p, data_fatura: e.target.value }))} style={inputStyle()} />
          </Campo>

          {/* IVA toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: form.com_iva ? '#fff8e1' : '#f0fff4', border: `1px solid ${form.com_iva ? C.warning : C.success}55`, borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
            <input type="checkbox" checked={form.com_iva} onChange={e => toggleIva(e.target.checked)} />
            <span>
              <strong>{form.com_iva ? 'Factura inclui IVA 23%' : 'Factura SEM IVA (intra-comunitária / OSS)'}</strong>
              <br/>
              <span style={{ color: C.textMuted, fontSize: '11px' }}>
                {form.com_iva
                  ? 'Sistema calcula sem_IVA + IVA a partir do total c/IVA.'
                  // O nome sai da ficha do fornecedor escolhido. Estava cravado
                  // "PLUS SKILL Ltd" e, com o segundo fornecedor seleccionado, o
                  // ecra dizia o nome do primeiro numa caixa que decide o IVA.
                  : `Factura de ${fornDaCompra?.nome || 'fornecedor intra-comunitário'}: valor total = valor sem IVA. IVA = 0.`}
              </span>
            </span>
          </label>

          {form.com_iva ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <Campo label="Sem IVA (€)">
                <input value={form.valor_sem_iva} onChange={e => onValorChange('valor_sem_iva', e.target.value)} placeholder="0.00" style={inputStyle()} />
              </Campo>
              <Campo label="IVA (€)">
                <input value={form.valor_iva} onChange={e => onValorChange('valor_iva', e.target.value)} placeholder="0.00" style={inputStyle()} />
              </Campo>
              <Campo label="Total c/IVA (€) *">
                <input value={form.valor_total} onChange={e => onValorChange('valor_total', e.target.value)} placeholder="0.00" style={{ ...inputStyle(), fontWeight: '700', color: C.primary }} />
              </Campo>
            </div>
          ) : (
            <Campo label="Valor total da factura (€) *">
              <input value={form.valor_total} onChange={e => onValorChange('valor_total', e.target.value)} placeholder="0.00" style={{ ...inputStyle(), fontWeight: '700', color: C.primary, fontSize: '15px' }} />
            </Campo>
          )}

          <Campo label="Notas">
            <textarea value={form.notas_admin} onChange={e => setForm(p => ({ ...p, notas_admin: e.target.value }))}
              rows={2} placeholder="(opcional)" style={{ ...inputStyle(), height: 'auto', padding: '8px 10px', resize: 'vertical' }} />
          </Campo>
          <p style={{ margin: 0, fontSize: '11px', color: C.textMuted }}>
            💡 Após criar, o detalhe abre automaticamente para linkares as encomendas cobertas.
          </p>
        </div>
        <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ background: '#fff', border: `1px solid ${C.border}`, padding: '8px 14px', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>Cancelar</button>
          <button onClick={submit} disabled={saving}
            style={{ background: C.primary, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '3px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'A criar...' : 'Criar compra'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal Detalhe (placeholder; expansão na sessão seguinte) ─────────────
function ModalDetalheCompra({ compra, fornecedores, onClose, onUpdated, onDeleted }) {
  const [pagModal, setPagModal] = useState(null) // { prestacao: '1a'|'2a' }
  const [uploadingFatura, setUploadingFatura] = useState(false)
  const [eliminando, setEliminando] = useState(false)
  // Re-validacao do PDF da factura existente (auditoria antes do pagamento)
  const [revalidando, setRevalidando] = useState(false)
  const [revalidacao, setRevalidacao] = useState(null) // { parsed, produtosBD } | null
  const [produtosBDLista, setProdutosBDLista] = useState([])
  useEffect(() => {
    let cancelado = false
    ;(async () => {
      const { data: prod } = await lerTudo(() => supabase.from('Produtos').select('id, Nome, EAN'), 'id')
      const { data: int } = await lerTudo(() => supabase.from('produtos_internos').select('produto_nome, preco_custo'), 'produto_nome')
      if (cancelado) return
      const eanPorNome = new Map((prod || []).map(p => [p.Nome, p.EAN ? String(p.EAN) : null]))
      setProdutosBDLista((int || []).map(r => ({
        nome: r.produto_nome,
        ean: eanPorNome.get(r.produto_nome) || null,
        preco_custo: r.preco_custo != null ? Number(r.preco_custo) : null,
      })))
    })()
    return () => { cancelado = true }
  }, [])

  const revalidarPdf = async () => {
    if (!compra.fatura_pdf_url) { alert('Esta compra nao tem PDF anexado.'); return }
    setRevalidando(true)
    try {
      const { data: blob, error } = await supabase.storage.from('faturas-fornecedor').download(compra.fatura_pdf_url)
      if (error) throw new Error(error.message)
      const parsed = await parseFaturaPdfFile(blob)
      setRevalidacao({ parsed })
    } catch (e) {
      alert('Erro ao re-validar PDF: ' + (e?.message || String(e)))
    } finally {
      setRevalidando(false)
    }
  }

  // Calcular validacao quando temos parsed
  const validacaoRev = useMemo(() => {
    if (!revalidacao?.parsed?.linhas?.length || !produtosBDLista.length) return null
    const porEan = new Map(produtosBDLista.filter(p => p.ean).map(p => [String(p.ean), p]))
    const porNome = new Map(produtosBDLista.map(p => [norm(p.nome), p]))
    let totalEsperado = 0
    let totalPdf = 0
    let temErro = false
    let temAviso = false
    const linhas = revalidacao.parsed.linhas.map(l => {
      const eanStr = l.ean ? String(l.ean) : ''
      const match = (eanStr && porEan.get(eanStr)) || porNome.get(norm(l.nome || '')) || null
      const precoBD = match?.preco_custo ?? null
      const precoPdf = Number(l.custo_unit_real || 0)
      const valLinhaPdf = Number(l.valor_total || 0)
      totalPdf += valLinhaPdf
      let estado = 'ok'
      let diff = 0
      if (precoBD == null) {
        estado = 'sem_custo'
      } else {
        diff = precoPdf - precoBD
        const diffPct = precoBD > 0 ? Math.abs(diff) / precoBD : 0
        if (Math.abs(diff) < 0.01) estado = 'ok'
        else if (diffPct <= 0.05) { estado = 'aviso'; temAviso = true }
        else { estado = 'erro'; temErro = true }
        totalEsperado += precoBD * Number(l.qty || 0)
      }
      return { ...l, preco_bd: precoBD, preco_pdf: precoPdf, diff, estado }
    })
    const semCusto = linhas.filter(l => l.estado === 'sem_custo').length
    const erros = linhas.filter(l => l.estado === 'erro').length
    const avisos = linhas.filter(l => l.estado === 'aviso').length
    const oks = linhas.filter(l => l.estado === 'ok').length
    const totalPdfReal = Number(revalidacao.parsed.valor_total || totalPdf)
    const ehFinal = compra.fase === 'final' && Number(compra.valor_final_total) > 0
    const totalBD = Number(
      ehFinal ? compra.valor_final_total : (compra.valor_total || 0)
    )
    const totalBDPrevisao = Number(compra.valor_total || 0)
    const diffTotal = totalPdfReal - totalEsperado
    const diffBD = totalPdfReal - totalBD
    // A prestacao sugerida sai da percentagem desta compra, nao de metade. Num
    // fornecedor que exige 100% a cabeca, sugerir metade poe o admin a
    // transferir metade do que deve — e a encomenda fica retida do lado deles
    // sem se perceber porque.
    const pctPrest = Number(compra.sinal_percentagem ?? 50) || 50
    const prestacao_sugerida = Number((totalPdfReal * pctPrest / 100).toFixed(2))
    return { linhas, totalPdf: totalPdfReal, totalEsperado, totalBD, totalBDPrevisao, ehFinal, diffTotal, diffBD, prestacao_sugerida, semCusto, erros, avisos, oks, temErro, temAviso }
  }, [revalidacao, produtosBDLista, compra.valor_total, compra.valor_final_total, compra.fase])

  const apagarCompra = async () => {
    const msg = `APAGAR a compra ${compra.numero_interno || compra.numero_fatura}?\n\n`
      + `Isto vai remover:\n`
      + `• A compra a fornecedor (${compra.numero_fatura})\n`
      + `• Todos os links com encomendas (cascade)\n`
      + `• Ficheiros PDF da factura/comprovativos no storage\n\n`
      + `As encomendas dos clientes NAO sao afectadas (so perdem o link a esta compra).\n\n`
      + `Continuar?`
    if (!window.confirm(msg)) return
    setEliminando(true)
    try {
      // Listar e apagar ficheiros do storage (best-effort)
      try {
        const { data: files } = await supabase.storage.from('faturas-fornecedor').list(compra.id)
        if (files && files.length > 0) {
          await supabase.storage.from('faturas-fornecedor').remove(files.map(f => `${compra.id}/${f.name}`))
        }
      } catch (e) { console.warn('Erro a apagar PDFs do storage:', e) }
      // Apagar compra (cascade apaga compras_fornecedor_encomendas)
      const { error } = await supabase.from('compras_fornecedor').delete().eq('id', compra.id)
      if (error) throw new Error(error.message)
      if (onDeleted) await onDeleted()
      onClose()
    } catch (e) {
      alert('Erro ao apagar: ' + (e?.message || String(e)))
    } finally {
      setEliminando(false)
    }
  }

  const uploadFatura = async (file) => {
    if (!file) return
    setUploadingFatura(true)
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
      const path = `${compra.id}/fatura.${ext}`
      const { error: upErr } = await supabase.storage.from('faturas-fornecedor').upload(path, file, { upsert: true, contentType: file.type })
      if (upErr) throw new Error(upErr.message)
      const { error } = await supabase.from('compras_fornecedor').update({ fatura_pdf_url: path, fatura_pdf_em: new Date().toISOString() }).eq('id', compra.id)
      if (error) throw new Error(error.message)
      await onUpdated()
    } catch (e) { alert('Erro: ' + e.message) }
    finally { setUploadingFatura(false) }
  }

  const verDoc = async (path) => {
    if (!path) return
    const { data, error } = await supabase.storage.from('faturas-fornecedor').createSignedUrl(path, 60 * 10)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
    else alert('Erro ao abrir: ' + (error?.message || 'desconhecido'))
  }

  // Calculo das prestacoes:
  //  - Base = valor_final_total se fase='final' (factura final com portes), senao valor_total (previsao)
  //  - 1a: se ja paga = pago_1a_valor; senao = base / 2
  //  - 2a: se ja paga = pago_2a_valor; senao = (1a paga ? base - pago_1a : base / 2)
  // Cobre 2 ajustes:
  //  a) total muda depois da 1a paga (e.g. apos remover item indisponivel)
  //  b) factura final adiciona portes que nao estavam na previsao
  const valorBase = compra.fase === 'final' && Number(compra.valor_final_total) > 0
    ? Number(compra.valor_final_total)
    : Number(compra.valor_total || 0)
  const pago1aValor = Number(compra.pago_1a_valor || 0)
  // Percentagem da 1.ª ao fornecedor. Histórico = 50. O acordo com o Plus Skill
  // a partir de 08/2026 é 20% do custo à cabeça — é o que permite baixar o sinal
  // do cliente. Configurável por compra em compras_fornecedor.sinal_percentagem.
  const sinalPctForn = Number(compra.sinal_percentagem ?? 50) || 50
  // Editável na ficha: obrigar a ir ao SQL todos os meses era garantir que um
  // dia se esquecia — e o painel de cobertura diria 2.500 EUR devidos quando
  // eram 1.000. Bloqueado depois da 1.ª paga: aí o valor real já está gravado
  // em pago_1a_valor e mexer na percentagem só confundia o histórico.
  const [pctInput, setPctInput] = useState(String(sinalPctForn))
  const [savingPct, setSavingPct] = useState(false)
  useEffect(() => { setPctInput(String(Number(compra.sinal_percentagem ?? 50) || 50)) }, [compra.sinal_percentagem])
  const guardarPct = async () => {
    const novo = Number(String(pctInput).replace(',', '.'))
    if (!Number.isFinite(novo) || novo <= 0 || novo > 100) { setPctInput(String(sinalPctForn)); return }
    const arred = Math.round(novo * 100) / 100
    if (arred === sinalPctForn) return
    setSavingPct(true)
    try {
      const { error } = await supabase.from('compras_fornecedor')
        .update({ sinal_percentagem: arred }).eq('id', compra.id)
      if (error) throw new Error(error.message)
      if (onUpdated) await onUpdated()
    } catch (e) {
      alert('Erro ao gravar a percentagem: ' + (e?.message || String(e)))
      setPctInput(String(sinalPctForn))
    } finally { setSavingPct(false) }
  }
  const metade = Math.round((valorBase * sinalPctForn / 100) * 100) / 100
  const valor1aSugerido = compra.pago_1a_prestacao ? pago1aValor : metade
  const valor2aSugerido = compra.pago_2a_prestacao
    ? Number(compra.pago_2a_valor || 0)
    : (compra.pago_1a_prestacao ? Math.max(0, valorBase - pago1aValor) : Math.max(0, valorBase - metade))

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.surface, borderRadius: '6px', maxWidth: '560px', width: '100%', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.bg }}>
          <div>
            <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Compra · {compra.numero_interno}</p>
            <h2 style={{ margin: '2px 0 0', fontSize: '15px', fontWeight: '700', color: C.text }}>{compra.fornecedor?.nome} · {compra.numero_fatura}</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}><X size={18} /></button>
        </div>
        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Resumo */}
          {(() => {
            const ehFinal = compra.fase === 'final' && Number(compra.valor_final_total) > 0
            const totalMostrar = ehFinal ? Number(compra.valor_final_total) : Number(compra.valor_total || 0)
            const semIvaMostrar = ehFinal && Number(compra.valor_final_sem_iva) > 0 ? Number(compra.valor_final_sem_iva) : Number(compra.valor_sem_iva || 0)
            const dif = ehFinal ? totalMostrar - Number(compra.valor_total || 0) : 0
            return (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                  <Info label="Data factura" valor={fmtDate(compra.data_fatura)} />
                  <Info label="Envio previsto" valor={fmtDate(compra.data_envio_prevista)} />
                  <Info
                    label="Total"
                    valor={`${fmt2(totalMostrar)} €`}
                    destaque
                    badge={ehFinal ? 'FINAL' : 'PREVISÃO'}
                    badgeCor={ehFinal ? C.success : C.warning}
                  />
                  <Info label="Sem IVA" valor={`${fmt2(semIvaMostrar)} €`} />
                </div>
                {ehFinal && dif !== 0 && (
                  <div style={{ marginTop: '-4px', fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>
                    Diferença face à previsão: <strong style={{ color: dif > 0 ? C.warning : C.success }}>{dif > 0 ? '+' : ''}{fmt2(dif)} €</strong> (portes / ajustes finais)
                  </div>
                )}
              </>
            )
          })()}

          {/* Factura PDF */}
          <div>
            <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Factura PDF do fornecedor</p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {compra.fatura_pdf_url ? (
                <button onClick={() => verDoc(compra.fatura_pdf_url)} style={{ background: C.success, color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={12} /> Ver factura</button>
              ) : (
                <span style={{ fontSize: '12px', color: C.textMuted }}>Por anexar</span>
              )}
              <label style={{ background: compra.fatura_pdf_url ? '#fff' : C.primary, color: compra.fatura_pdf_url ? C.primary : '#fff', border: `1px solid ${C.primary}`, padding: '6px 12px', borderRadius: '3px', cursor: uploadingFatura ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Upload size={12} /> {uploadingFatura ? 'A enviar...' : (compra.fatura_pdf_url ? 'Substituir' : 'Anexar')}
                <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} disabled={uploadingFatura}
                  onChange={(ev) => { const f = ev.target.files?.[0]; if (f) uploadFatura(f); ev.target.value = '' }} />
              </label>
              {compra.fatura_pdf_url && (
                <button onClick={revalidarPdf} disabled={revalidando} title="Re-validar valores do PDF contra a BD (preços de custo + total). Útil antes de pagar prestações."
                  style={{ background: '#FACC15', color: '#0a0a0a', border: 'none', padding: '6px 12px', borderRadius: '3px', cursor: revalidando ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <FileSearch size={12} /> {revalidando ? 'A validar...' : 'Re-validar PDF'}
                </button>
              )}
            </div>
          </div>

          {/* Prestações ao fornecedor — a divisão é por factura, não global:
              só as facturas com os artigos do acordo vão a 20%. */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
              <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>
                Pagamentos ao fornecedor ({sinalPctForn}% + {Math.round((100 - sinalPctForn) * 10) / 10}%)
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ fontSize: '10.5px', color: C.textMuted }} title="Percentagem que o fornecedor pede à cabeça NESTA factura. 50% é o normal; 20% nas facturas com os artigos do acordo.">1.ª prestação:</span>
                <input
                  type="number" step="1" min="1" max="100"
                  value={pctInput}
                  onChange={e => setPctInput(e.target.value)}
                  onBlur={guardarPct}
                  onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
                  disabled={savingPct || compra.pago_1a_prestacao}
                  title={compra.pago_1a_prestacao ? 'A 1.ª prestação já foi paga — a percentagem deixa de ter efeito' : 'Escreve e sai do campo para gravar'}
                  style={{ width: '58px', height: '26px', padding: '0 6px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px', fontWeight: '700', textAlign: 'right', color: C.text, outline: 'none', opacity: (savingPct || compra.pago_1a_prestacao) ? 0.55 : 1 }} />
                <span style={{ fontSize: '11px', fontWeight: '700', color: C.textMuted }}>%</span>
                {savingPct && <span style={{ fontSize: '10px', color: C.textMuted }}>a gravar…</span>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {['1a', '2a'].map(prest => {
                const pago = prest === '1a' ? compra.pago_1a_prestacao : compra.pago_2a_prestacao
                const data = prest === '1a' ? compra.pago_1a_data : compra.pago_2a_data
                const banco = prest === '1a' ? compra.pago_1a_banco : compra.pago_2a_banco
                const valor = prest === '1a' ? compra.pago_1a_valor : compra.pago_2a_valor
                const url = prest === '1a' ? compra.comprovativo_1a_url : compra.comprovativo_2a_url
                const valorMostrar = prest === '1a' ? valor1aSugerido : valor2aSugerido
                const ajustado = prest === '2a' && !pago && compra.pago_1a_prestacao && Math.abs(valor2aSugerido - metade) > 0.01
                return (
                  <div key={prest}
                    style={{ border: `1px solid ${pago ? C.success : C.border}`, background: pago ? '#e8f5e9' : C.surface, borderRadius: '4px', padding: '10px 12px', cursor: 'pointer' }}
                    onClick={() => setPagModal({ prestacao: prest })}>
                    <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.text }}>{prest === '1a' ? '1.ª Prestação' : '2.ª Prestação'} ({prest === '1a' ? sinalPctForn : Math.round((100 - sinalPctForn) * 10) / 10}%)</p>
                    {pago ? (
                      <>
                        <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: '700', color: C.success }}>{fmt2(valor || valorMostrar)} €</p>
                        <p style={{ margin: '2px 0 0', fontSize: '11px', color: C.textMuted }}>{fmtDate(data)}{banco ? ` · ${banco}` : ''}</p>
                        {url && (
                          <button onClick={(e) => { e.stopPropagation(); verDoc(url) }} style={{ marginTop: '6px', background: 'transparent', border: `1px solid ${C.success}`, color: C.success, padding: '3px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Eye size={10} /> Comprovativo</button>
                        )}
                      </>
                    ) : (
                      <>
                        <p style={{ margin: '4px 0 0', fontSize: '14px', fontWeight: '700', color: C.warning }}>{fmt2(valorMostrar)} €</p>
                        <p style={{ margin: '2px 0 0', fontSize: '11px', color: C.warning, fontWeight: '600' }}>Por pagar — clica para registar</p>
                        {ajustado && (
                          <p style={{ margin: '4px 0 0', fontSize: '10px', color: C.textMuted, fontStyle: 'italic' }}>= Total − 1.ª paga</p>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {compra.notas_admin && (
            <div>
              <p style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Notas</p>
              <p style={{ margin: 0, fontSize: '13px', color: C.text, whiteSpace: 'pre-wrap' }}>{compra.notas_admin}</p>
            </div>
          )}

          {/* O que esta factura ainda tem por chegar */}
          <SecaoTransitosPendentes compra={compra} onUpdated={onUpdated} />

          {/* Encomendas linkadas + lucro real */}
          <SecaoLinkEncomendas compra={compra} onUpdated={onUpdated} />

          {/* Fase 2 — Factura final */}
          <SecaoFase2 compra={compra} onUpdated={onUpdated} />

          {/* ZONA PERIGOSA — apagar compra */}
          <div style={{ marginTop: '4px', padding: '10px 12px', background: '#fff5f5', border: `1px solid ${C.danger}33`, borderRadius: '4px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.danger, textTransform: 'uppercase' }}>⚠️ Zona perigosa</p>
            <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.text }}>
              Apagar esta compra remove tambem todos os links a encomendas e o PDF do storage.
              As encomendas dos clientes mantem-se (so perdem o link).
            </p>
            <button onClick={apagarCompra} disabled={eliminando}
              style={{ background: '#fff', color: C.danger, border: `1px solid ${C.danger}`, padding: '6px 12px', borderRadius: '3px', cursor: eliminando ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '5px', opacity: eliminando ? 0.7 : 1 }}>
              <Trash2 size={12} /> {eliminando ? 'A apagar…' : 'Apagar compra'}
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modal: registar pagamento de prestação */}
      {pagModal && (
        <ModalPagamentoPrestacao
          compra={compra}
          prestacao={pagModal.prestacao}
          metadeSugerida={pagModal.prestacao === '1a' ? valor1aSugerido : valor2aSugerido}
          onClose={() => setPagModal(null)}
          onSaved={async () => { setPagModal(null); await onUpdated() }}
        />
      )}

      {/* Sub-modal: auditoria/re-validação do PDF antes de pagar prestação */}
      {revalidacao && validacaoRev && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={(e) => { if (e.target === e.currentTarget) setRevalidacao(null) }}>
          <div style={{ background: C.surface, borderRadius: '8px', width: '100%', maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ background: '#0a0a0a', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, color: '#FACC15', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>Auditoria · {compra.numero_fatura}</p>
                <h2 style={{ color: '#fff', margin: '3px 0 0', fontSize: '16px', fontWeight: '700' }}>Re-validação do PDF da factura</h2>
              </div>
              <button onClick={() => setRevalidacao(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', padding: '6px' }}><X size={16} /></button>
            </div>
            <div style={{ padding: '14px 20px', overflow: 'auto', flex: 1 }}>
              <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '700', color: validacaoRev.temErro ? C.danger : validacaoRev.temAviso ? '#92400e' : '#107c10', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {validacaoRev.temErro ? <AlertTriangle size={14} /> : validacaoRev.temAviso ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
                {validacaoRev.oks} ✓ OK · {validacaoRev.avisos} ⚠ Pequena diff · {validacaoRev.erros} 🚫 Grande diff · {validacaoRev.semCusto} ❓ Sem custo BD
              </p>
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: '3px', background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead>
                    <tr style={{ background: C.bg, position: 'sticky', top: 0 }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>EAN</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left' }}>Produto</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Qty</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>€/un PDF</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>€/un BD</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right' }}>Diff</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validacaoRev.linhas.map(l => {
                      const bgRow = l.estado === 'erro' ? '#fde7e955' : l.estado === 'aviso' ? '#fff4ce55' : l.estado === 'sem_custo' ? '#f3f0ff55' : 'transparent'
                      const corEstado = l.estado === 'erro' ? C.danger : l.estado === 'aviso' ? '#c07400' : l.estado === 'sem_custo' ? '#6264a7' : C.success
                      const labelEstado = l.estado === 'erro' ? '🚫' : l.estado === 'aviso' ? '⚠' : l.estado === 'sem_custo' ? '❓' : '✓'
                      return (
                        <tr key={l.idx} style={{ borderTop: `1px solid ${C.border}`, background: bgRow }}>
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{l.ean || '?'}</td>
                          <td style={{ padding: '6px 8px' }}>{(l.nome || '—').slice(0, 36)}{(l.nome?.length || 0) > 36 ? '…' : ''}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>{l.qty}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700' }}>{l.preco_pdf.toFixed(2)}€</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: l.preco_bd == null ? C.textMuted : C.text }}>{l.preco_bd == null ? '—' : l.preco_bd.toFixed(2) + '€'}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', color: corEstado, fontWeight: '700' }}>
                            {l.preco_bd == null ? '—' : (l.diff > 0 ? '+' : '') + l.diff.toFixed(2) + '€'}
                          </td>
                          <td style={{ padding: '6px 8px', textAlign: 'center', color: corEstado, fontWeight: '700' }}>{labelEstado}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                <div style={{ background: '#fff', padding: '8px 10px', borderRadius: '3px', border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Total PDF (parser)</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: C.text }}>{validacaoRev.totalPdf.toFixed(2)} €</div>
                </div>
                <div style={{ background: '#fff', padding: '8px 10px', borderRadius: '3px', border: `1px solid ${C.border}` }}>
                  <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    Total BD (gravado)
                    <span style={{ padding: '1px 5px', fontSize: '9px', fontWeight: '800', background: validacaoRev.ehFinal ? C.success : C.warning, color: '#fff', borderRadius: '3px' }}>
                      {validacaoRev.ehFinal ? 'FINAL' : 'PREVISÃO'}
                    </span>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: C.text }}>{validacaoRev.totalBD.toFixed(2)} €</div>
                  {validacaoRev.ehFinal && Math.abs(validacaoRev.totalBD - validacaoRev.totalBDPrevisao) > 0.01 && (
                    <div style={{ fontSize: '10px', color: C.textMuted, marginTop: '2px' }}>
                      Previsão: {validacaoRev.totalBDPrevisao.toFixed(2)} €
                    </div>
                  )}
                </div>
                <div style={{ background: '#fff', padding: '8px 10px', borderRadius: '3px', border: `1px solid ${Math.abs(validacaoRev.diffBD) > 5 ? C.danger : C.success}` }}>
                  <div style={{ color: C.textMuted, fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Diff PDF vs BD</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: Math.abs(validacaoRev.diffBD) > 5 ? C.danger : '#107c10' }}>
                    {validacaoRev.diffBD > 0 ? '+' : ''}{validacaoRev.diffBD.toFixed(2)} €
                  </div>
                </div>
                <div style={{ background: '#0a0a0a', padding: '8px 10px', borderRadius: '3px', color: '#fff' }}>
                  <div style={{ color: '#FACC15', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>1.ª Prestação (50%)</div>
                  <div style={{ fontSize: '14px', fontWeight: '700' }}>{validacaoRev.prestacao_sugerida.toFixed(2)} €</div>
                </div>
              </div>
              {Math.abs(validacaoRev.diffBD) < 0.5 ? (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: '#dff6dd', border: `2px solid ${C.success}`, borderRadius: '4px', fontSize: '13px', color: C.success, display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}>
                  <CheckCircle size={16} />
                  <span>Totais batem: PDF {validacaoRev.totalPdf.toFixed(2)} € = BD {validacaoRev.ehFinal ? '(FINAL)' : '(previsão)'} {validacaoRev.totalBD.toFixed(2)} € ✓ Pronto a pagar.</span>
                </div>
              ) : Math.abs(validacaoRev.diffBD) > 5 ? (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fde7e9', border: `1px solid ${C.danger}`, borderRadius: '4px', fontSize: '12px', color: C.danger }}>
                  <strong>⚠ ATENÇÃO:</strong> Total do PDF ({validacaoRev.totalPdf.toFixed(2)} €) diverge {Math.abs(validacaoRev.diffBD).toFixed(2)} € do valor gravado na BD {validacaoRev.ehFinal ? '(FINAL)' : '(PREVISÃO)'} ({validacaoRev.totalBD.toFixed(2)} €). {!validacaoRev.ehFinal && 'Se o PDF já é a factura final, actualiza a fase para "final" antes de pagar.'} Corrige antes de pagar.
                </div>
              ) : (
                <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fff4ce', border: `1px solid ${C.warning}55`, borderRadius: '3px', fontSize: '12px', color: '#92400e' }}>
                  <strong>Pequena diferença:</strong> {Math.abs(validacaoRev.diffBD).toFixed(2)} € entre PDF e BD {validacaoRev.ehFinal ? '(FINAL)' : '(previsão)'}. Provavelmente arredondamentos — verifica antes de pagar.
                </div>
              )}
              {revalidacao.parsed.avisos?.length > 0 && (
                <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fff4ce', border: `1px solid ${C.warning}55`, borderRadius: '3px', fontSize: '11px', color: '#92400e' }}>
                  {revalidacao.parsed.avisos.map((a, i) => <div key={i}>⚠ {a}</div>)}
                </div>
              )}
            </div>
            <div style={{ padding: '10px 18px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px', background: C.bg }}>
              <button onClick={() => setRevalidacao(null)} style={{ height: '34px', padding: '0 14px', border: `1px solid ${C.border}`, borderRadius: '4px', background: C.surface, color: C.text, fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Info({ label, valor, destaque, badge, badgeCor }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: '10px', color: C.textMuted, textTransform: 'uppercase', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {label}
        {badge && (
          <span style={{ padding: '1px 6px', fontSize: '9px', fontWeight: '800', letterSpacing: '0.3px', background: badgeCor || C.success, color: '#fff', borderRadius: '3px', textTransform: 'uppercase' }}>{badge}</span>
        )}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: destaque ? '15px' : '13px', fontWeight: destaque ? '800' : '600', color: destaque ? C.primary : C.text }}>{valor}</p>
    </div>
  )
}

function Campo({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</label>
      {children}
    </div>
  )
}

const inputStyle = () => ({
  width: '100%', height: '34px', padding: '0 10px',
  border: `1px solid ${C.border}`, borderRadius: '3px',
  fontSize: '13px', color: C.text, outline: 'none', boxSizing: 'border-box',
  background: '#fff',
})

// ─── Sub-modal: registar pagamento de prestação + upload comprovativo ─────
function ModalPagamentoPrestacao({ compra, prestacao, metadeSugerida, onClose, onSaved }) {
  const [form, setForm] = useState({
    file: null,
    banco: prestacao === '1a' ? (compra.pago_1a_banco || '') : (compra.pago_2a_banco || ''),
    data: prestacao === '1a' ? (compra.pago_1a_data || new Date().toISOString().slice(0,10)) : (compra.pago_2a_data || new Date().toISOString().slice(0,10)),
    valor: prestacao === '1a' ? (compra.pago_1a_valor || metadeSugerida.toFixed(2)) : (compra.pago_2a_valor || metadeSugerida.toFixed(2)),
  })
  const [saving, setSaving] = useState(false)
  const [erro, setErro] = useState('')
  const [bancoOpen, setBancoOpen] = useState(false)

  const submit = async () => {
    if (!form.banco.trim()) { setErro('Indica o banco.'); return }
    if (!form.data) { setErro('Indica a data.'); return }
    if (!form.valor || Number(form.valor) <= 0) { setErro('Valor tem de ser > 0.'); return }
    setErro(''); setSaving(true)
    try {
      let path = prestacao === '1a' ? compra.comprovativo_1a_url : compra.comprovativo_2a_url
      if (form.file) {
        const ext = (form.file.name.split('.').pop() || 'pdf').toLowerCase()
        path = `${compra.id}/comprovativo-${prestacao}.${ext}`
        const { error: upErr } = await supabase.storage.from('faturas-fornecedor').upload(path, form.file, { upsert: true, contentType: form.file.type })
        if (upErr) throw new Error(upErr.message)
      }
      const now = new Date().toISOString()
      const update = prestacao === '1a' ? {
        pago_1a_prestacao: true,
        pago_1a_data: form.data,
        pago_1a_banco: form.banco.trim(),
        pago_1a_valor: Number(form.valor),
        comprovativo_1a_url: path || null,
        comprovativo_1a_em: form.file ? now : (compra.comprovativo_1a_em || null),
      } : {
        pago_2a_prestacao: true,
        pago_2a_data: form.data,
        pago_2a_banco: form.banco.trim(),
        pago_2a_valor: Number(form.valor),
        comprovativo_2a_url: path || null,
        comprovativo_2a_em: form.file ? now : (compra.comprovativo_2a_em || null),
      }
      const { error } = await supabase.from('compras_fornecedor').update(update).eq('id', compra.id)
      if (error) throw new Error(error.message)
      await onSaved()
    } catch (e) { setErro('Erro: ' + e.message) }
    finally { setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose() }}>
      <div style={{ background: C.surface, borderRadius: '6px', maxWidth: '420px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.success, color: '#fff' }}>
          <p style={{ margin: 0, fontSize: '13px', fontWeight: '700' }}>Registar {prestacao === '1a' ? '1.ª' : '2.ª'} Prestação · {compra.numero_interno}</p>
          <button onClick={() => !saving && onClose()} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '2px' }}><X size={16} /></button>
        </div>
        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {erro && <div style={{ padding: '8px 12px', background: '#fde7e9', border: `1px solid ${C.danger}`, borderRadius: '3px', fontSize: '12px', color: C.danger }}>{erro}</div>}
          <Campo label="Comprovativo do pagamento (PDF/imagem)">
            <input type="file" accept="application/pdf,image/*" disabled={saving}
              onChange={e => setForm(p => ({ ...p, file: e.target.files?.[0] || null }))}
              style={{ width: '100%', fontSize: '12px' }} />
            {form.file && <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.success, fontWeight: '600' }}>✓ {form.file.name}</p>}
          </Campo>
          <Campo label="Banco de onde saiu *">
            <div style={{ position: 'relative' }}>
              <input
                value={form.banco}
                onChange={e => { setForm(p => ({ ...p, banco: e.target.value })); setBancoOpen(true) }}
                onFocus={() => setBancoOpen(true)}
                onBlur={() => setTimeout(() => setBancoOpen(false), 150)}
                placeholder="Comece a escrever: Mil, Sant, BPI, Revolut, ..."
                autoComplete="off"
                style={inputStyle()}
                disabled={saving}
              />
              {bancoOpen && (() => {
                const q = (form.banco || '').trim().toLowerCase()
                const sugestoes = q.length === 0
                  ? BANCOS_PT.slice(0, 8)
                  : BANCOS_PT.filter(b => b.toLowerCase().includes(q)).slice(0, 8)
                if (sugestoes.length === 0) return null
                return (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '2px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 6px 18px rgba(0,0,0,0.10)', zIndex: 10, maxHeight: '240px', overflowY: 'auto' }}>
                    {sugestoes.map(b => (
                      <div
                        key={b}
                        onMouseDown={e => { e.preventDefault(); setForm(p => ({ ...p, banco: b })); setBancoOpen(false) }}
                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: C.text, borderBottom: `1px solid ${C.bg}` }}
                        onMouseEnter={e => e.currentTarget.style.background = C.bg}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >{b}</div>
                    ))}
                    {q.length > 0 && !BANCOS_PT.some(b => b.toLowerCase() === q) && (
                      <div style={{ padding: '6px 12px', fontSize: '11px', color: C.textMuted, fontStyle: 'italic', background: C.bg }}>
                        ou continua a escrever um banco que não está na lista
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </Campo>
          <Campo label="Data do pagamento *">
            <input type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data: e.target.value }))} style={inputStyle()} disabled={saving} />
          </Campo>
          <Campo label="Valor pago (€) *">
            <input value={form.valor} onChange={e => setForm(p => ({ ...p, valor: e.target.value.replace(',','.').replace(/[^0-9.]/g,'') }))} style={inputStyle()} disabled={saving} />
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: C.textMuted }}>Sugerido: {fmt2(metadeSugerida)} €</p>
          </Campo>
        </div>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={() => !saving && onClose()} disabled={saving}
            style={{ background: '#fff', border: `1px solid ${C.border}`, padding: '6px 14px', borderRadius: '3px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px' }}>Cancelar</button>
          <button onClick={submit} disabled={saving}
            style={{ background: C.success, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '3px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'A guardar...' : 'Confirmar pagamento'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Secao: linkar encomendas a esta compra ────────────────────────────────
// ─── O que esta factura ainda tem por chegar ────────────────────────────────
//
// Um artigo comprado fica com Transito_Qtd > 0 e Transito_Factura = o numero
// desta factura, ate a LOGNOW confirmar a entrada. Enquanto isso, a factura
// conta como "em transito": nao arquiva, e a cron avisa todos os dias.
//
// Quando a mercadoria simplesmente NAO VEIO — caixa em falta, o fornecedor
// confirma e manda na proxima — nao havia saida nenhuma. Limpar o transito a
// mao desbloqueava tudo mas apagava a divida: deixava de haver em lado nenhum a
// informacao de que ainda nos devem aquilo.
//
// Este painel faz as duas coisas de uma vez: tira do transito e escreve a
// divida em faltas_fornecedor, onde se pode consultar e dar por recebida.
function SecaoTransitosPendentes({ compra, onUpdated }) {
  const [pendentes, setPendentes] = useState([])
  const [faltas, setFaltas] = useState([])
  const [loading, setLoading] = useState(true)
  const [aGravar, setAGravar] = useState(null)

  const numero = compra.numero_fatura_final || compra.numero_fatura

  // Artigos desta factura cujo transito ainda nao tem factura nenhuma.
  //
  // Nada na aplicacao escreveu alguma vez o Transito*_Factura: os numeros que la
  // estao foram postos a mao, por SQL. Sem isto, uma factura nova nasce sempre
  // com o painel vazio — o stock vem a caminho e a factura nao sabe que e dela.
  const [semFactura, setSemFactura] = useState([])

  const carregar = async () => {
    setLoading(true)
    if (!numero) { setPendentes([]); setFaltas([]); setSemFactura([]); setLoading(false); return }
    // Os tres slots de transito sao colunas, nao linhas: nao da para filtrar
    // com um `eq` so. Traz-se quem tenha esta factura em qualquer um deles.
    const [{ data: prods }, { data: fs }] = await Promise.all([
      supabase.from('Produtos')
        .select('id, Nome, EAN, ' + SLOTS_TRANSITO.map(n => `Transito${n}_Qtd, Transito${n}_Data, Transito${n}_Factura`).join(', '))
        .or(SLOTS_TRANSITO.map(n => `Transito${n}_Factura.eq.${numero}`).join(',')),
      supabase.from('faltas_fornecedor')
        .select('*').eq('numero_fatura', numero).order('registado_em', { ascending: false }),
    ])
    const linhas = []
    for (const p of (prods || [])) {
      for (const slot of [1, 2, 3]) {
        const qtd = Number(p[`Transito${slot}_Qtd`] || 0)
        if (qtd > 0 && String(p[`Transito${slot}_Factura`] || '') === String(numero)) {
          linhas.push({ produto: p, slot, qtd, data: p[`Transito${slot}_Data`] })
        }
      }
    }
    setPendentes(linhas)
    setFaltas(fs || [])

    // Candidatos a atribuicao: os artigos das encomendas ligadas a esta compra
    // que tenham transito por etiquetar. So estes — nao se anda a varrer o
    // catalogo a procura de trânsitos orfãos que podem ser de outra factura.
    const { data: links } = await supabase.from('compras_fornecedor_encomendas')
      .select('produto_nome').eq('compra_id', compra.id)
    const nomes = [...new Set((links || []).map(l => l.produto_nome).filter(Boolean))]
    if (nomes.length === 0) { setSemFactura([]); setLoading(false); return }
    const { data: orfaos } = await supabase.from('Produtos')
      .select('id, Nome, EAN, ' + SLOTS_TRANSITO.map(n => `Transito${n}_Qtd, Transito${n}_Data, Transito${n}_Factura`).join(', '))
      .in('Nome', nomes)
    const porEtiquetar = []
    for (const p of (orfaos || [])) {
      for (const slot of [1, 2, 3]) {
        const qtd = Number(p[`Transito${slot}_Qtd`] || 0)
        if (qtd > 0 && !p[`Transito${slot}_Factura`]) {
          porEtiquetar.push({ produto: p, slot, qtd, data: p[`Transito${slot}_Data`] })
        }
      }
    }
    setSemFactura(porEtiquetar)
    setLoading(false)
  }
  useEffect(() => { carregar() }, [numero, compra.id])

  const atribuirFactura = async () => {
    if (semFactura.length === 0) return
    const totalUn = semFactura.reduce((s, l) => s + l.qtd, 0)
    if (!window.confirm(
      `Marcar ${semFactura.length} trânsito(s) — ${totalUn} unidades — como sendo da factura ${numero}?\n\n` +
      `Só toca em trânsitos que estão sem factura nenhuma, de artigos desta compra.\n` +
      `Nenhuma quantidade nem data é alterada.`
    )) return
    setAGravar('atribuir')
    try {
      for (const l of semFactura) {
        const { error } = await supabase.from('Produtos').update({
          [`Transito${l.slot}_Factura`]: String(numero),
          // A data original passa a ser a prevista: e dela que a cron dos
          // atrasos conta os dias. Sem isto, um transito acabado de etiquetar
          // podia nascer ja "atrasado" por causa de uma data antiga.
          [`Transito${l.slot}_Data_Original`]: l.data || null,
        }).eq('id', l.produto.id)
        if (error) throw new Error(error.message)
      }
      await carregar()
      onUpdated?.()
    } catch (e) {
      alert('Erro: ' + (e.message || String(e)))
    } finally { setAGravar(null) }
  }

  const marcarNaoVeio = async (linha) => {
    const { produto, slot, qtd } = linha
    if (!window.confirm(
      `"${produto.Nome}"\n${qtd} unidade(s) desta factura não chegaram.\n\n` +
      `Isto vai:\n` +
      `  • tirar as ${qtd} un do trânsito (o aviso diário pára e a factura passa a poder fechar)\n` +
      `  • registar que o fornecedor te deve estas ${qtd} un\n\n` +
      `O custo NÃO se mexe: já foi facturado e pago nesta factura. Só a mercadoria vem mais tarde.\n\nConfirmas?`
    )) return
    setAGravar(`${produto.id}-${slot}`)
    try {
      const { data: sess } = await supabase.auth.getUser()
      const { error: errF } = await supabase.from('faltas_fornecedor').insert({
        compra_id: compra.id,
        numero_fatura: numero,
        fornecedor_id: compra.fornecedor_id || null,
        produto_id: produto.id,
        produto_nome: produto.Nome,
        produto_ean: produto.EAN ? String(produto.EAN) : null,
        quantidade: qtd,
        registado_por: sess?.user?.id || null,
        nota: 'Não veio nesta factura. Fornecedor envia na próxima.',
      })
      if (errF) throw new Error(errF.message)
      // Só depois de a dívida estar gravada é que se larga o trânsito. Ao
      // contrário, uma falha a meio deixava a factura desbloqueada e a dívida
      // por registar — que é exactamente o estado que isto vem evitar.
      const { error: errP } = await supabase.from('Produtos')
        .update({ [`Transito${slot}_Qtd`]: 0 })
        .eq('id', produto.id)
      if (errP) throw new Error(errP.message)
      await carregar()
      onUpdated?.()
    } catch (e) {
      alert('Erro: ' + (e.message || String(e)))
    } finally { setAGravar(null) }
  }

  const marcarRecebida = async (falta) => {
    const fatura = window.prompt(
      `"${falta.produto_nome}" — ${falta.quantidade} un.\n\n` +
      `Em que factura chegou? (deixa vazio se chegou sem factura nova)`, '')
    if (fatura === null) return
    setAGravar(`falta-${falta.id}`)
    const { error } = await supabase.from('faltas_fornecedor')
      .update({ resolvido_em: new Date().toISOString(), resolvido_fatura: fatura.trim() || null })
      .eq('id', falta.id)
    setAGravar(null)
    if (error) { alert('Erro: ' + error.message); return }
    await carregar()
  }

  const abertas = faltas.filter(f => !f.resolvido_em)
  // Uma remessa com data no futuro NAO falhou — ainda nem e devida. Misturar as
  // duas na mesma lista poe o botao "Nao veio" ao lado de mercadoria que esta
  // simplesmente a caminho, e um clique enganado tira do transito 10 unidades
  // que vao chegar na mesma. Separa-se por data.
  const hojeISO = new Date().toISOString().slice(0, 10)
  const emAtraso = pendentes.filter(l => !l.data || String(l.data).slice(0, 10) <= hojeISO)
  const aCaminho = pendentes.filter(l => l.data && String(l.data).slice(0, 10) > hojeISO)

  if (loading) return null
  if (pendentes.length === 0 && faltas.length === 0 && semFactura.length === 0) return null

  const linhaProduto = (l, atrasada) => (
    <div key={`${l.produto.id}-${l.slot}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', background: C.surface, border: `1px solid ${atrasada ? `${C.warning}66` : C.border}`, borderRadius: '4px', padding: '8px 10px' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{l.produto.Nome}</div>
        <div style={{ fontSize: '11px', color: atrasada ? '#92400e' : C.textMuted }}>
          {l.qtd} un · {atrasada ? 'devia ter chegado' : 'previsto'} {l.data ? new Date(l.data).toLocaleDateString('pt-PT') : '—'}
          {l.produto.EAN ? ` · ${l.produto.EAN}` : ''}
        </div>
      </div>
      {atrasada ? (
        <button onClick={() => marcarNaoVeio(l)} disabled={aGravar != null}
          title="A mercadoria não chegou. Tira do trânsito e regista que o fornecedor te deve."
          style={{ height: '30px', padding: '0 12px', border: `1px solid ${C.warning}`, borderRadius: '3px', background: '#fff8e1', color: '#92400e', cursor: aGravar ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
          {aGravar === `${l.produto.id}-${l.slot}` ? 'A gravar…' : 'Não veio →'}
        </button>
      ) : (
        <span style={{ fontSize: '11px', color: C.textMuted, fontStyle: 'italic', whiteSpace: 'nowrap' }}>a caminho</span>
      )}
    </div>
  )

  return (
    <div style={{ marginTop: '4px', padding: '12px 14px', background: '#fffbf0', border: `1px solid #f0d070`, borderRadius: '4px' }}>
      <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Por chegar nesta factura
      </p>

      {pendentes.length === 0 ? (
        <p style={{ margin: '0 0 10px', fontSize: '12px', color: C.success, fontWeight: '600' }}>
          ✓ Nada em trânsito — esta factura já pode fechar.
        </p>
      ) : (
        <div style={{ marginBottom: abertas.length ? '12px' : '0' }}>
          {emAtraso.length > 0 && (
            <>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#92400e' }}>
                Em atraso — a data já passou
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: aCaminho.length ? '10px' : '0' }}>
                {emAtraso.map(l => linhaProduto(l, true))}
              </div>
            </>
          )}
          {aCaminho.length > 0 && (
            <>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: C.textMuted }}>
                A caminho — ainda dentro do prazo
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {aCaminho.map(l => linhaProduto(l, false))}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>
                Enquanto houver mercadoria a caminho, esta factura não fecha — e é correcto que não feche.
              </p>
            </>
          )}
        </div>
      )}

      {semFactura.length > 0 && (
        <div style={{ margin: '0 0 12px', padding: '10px 12px', background: '#eff6fc', border: `1px solid #c7e0f4`, borderRadius: '4px' }}>
          <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: C.primary }}>
            {semFactura.length} trânsito(s) desta compra sem factura atribuída
          </p>
          <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
            {semFactura.reduce((s, l) => s + l.qtd, 0)} unidades a caminho, de artigos desta compra, que não estão
            ligadas a factura nenhuma. Enquanto assim for, esta factura não sabe que tem stock por entrar.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '8px' }}>
            {semFactura.slice(0, 8).map(l => (
              <div key={`sf-${l.produto.id}-${l.slot}`} style={{ fontSize: '11px', color: C.textMuted }}>
                • {l.produto.Nome} — {l.qtd} un
                {l.data ? ` · ${new Date(l.data).toLocaleDateString('pt-PT')}` : ''}
              </div>
            ))}
            {semFactura.length > 8 && (
              <div style={{ fontSize: '11px', color: C.textMuted, fontStyle: 'italic' }}>… e mais {semFactura.length - 8}</div>
            )}
          </div>
          <button onClick={atribuirFactura} disabled={aGravar != null}
            style={{ height: '30px', padding: '0 12px', border: 'none', borderRadius: '3px', background: C.primary, color: '#fff', cursor: aGravar ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
            {aGravar === 'atribuir' ? 'A atribuir…' : `Atribuir à factura ${numero}`}
          </button>
        </div>
      )}

      {abertas.length > 0 && (
        <>
          <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            O fornecedor deve-te
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {abertas.map(f => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', background: '#fff8e1', border: `1px solid ${C.warning}55`, borderRadius: '4px', padding: '8px 10px' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: C.text }}>{f.produto_nome}</div>
                  <div style={{ fontSize: '11px', color: C.textMuted }}>
                    {f.quantidade} un · em falta desde {new Date(f.registado_em).toLocaleDateString('pt-PT')}
                  </div>
                </div>
                <button onClick={() => marcarRecebida(f)} disabled={aGravar != null}
                  style={{ height: '30px', padding: '0 12px', border: 'none', borderRadius: '3px', background: C.success, color: '#fff', cursor: aGravar ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                  {aGravar === `falta-${f.id}` ? 'A gravar…' : '✓ Já chegou'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {faltas.some(f => f.resolvido_em) && (
        <p style={{ margin: '10px 0 0', fontSize: '11px', color: C.textMuted }}>
          {faltas.filter(f => f.resolvido_em).length} falta(s) desta factura já resolvida(s).
        </p>
      )}
    </div>
  )
}

function SecaoLinkEncomendas({ compra, onUpdated }) {
  const [links, setLinks] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [itensDisponiveis, setItensDisponiveis] = useState([])
  const [loading, setLoading] = useState(true)
  const isFinal = compra.fase === 'final'
  // ─── O que da factura não é de cliente nenhum ─────────────────────────────
  // Em cada encomenda ao fornecedor vêm artigos que não são para vender: uns
  // para consumo, outros para revenda em marketplace. Se ficarem sem dono, o
  // sistema vê mercadoria comprada que nunca é vendida e a margem do fornecedor
  // aparece pior do que é — para sempre, porque nada os vai reclamar.
  //
  // Vão para a conta interna, ao custo. Não é venda, não gera lucro, e sai dos
  // cálculos de margem. Se mais tarde algum for vendido num marketplace,
  // regista-se lá a venda: o custo já está lançado aqui.
  const [sobras, setSobras] = useState(null)   // null = ainda não se procurou
  // Por artigo: 'consumo' (fica no catálogo de todos) ou 'revenda' (reservado
  // para mim). A RPC já não reclama nada nas encomendas internas — quem decide
  // é quem está a olhar para a lista, artigo a artigo, e é aqui que se decide.
  const [destino, setDestino] = useState({})
  const [aProcurar, setAProcurar] = useState(false)
  const [aCriarInterna, setACriarInterna] = useState(false)
  const [perfisInternos, setPerfisInternos] = useState([])
  const [internoEscolhido, setInternoEscolhido] = useState('')

  useEffect(() => {
    supabase.from('perfis').select('id, nome, email').eq('cliente_interno', true).order('nome')
      .then(({ data }) => {
        setPerfisInternos(data || [])
        if ((data || []).length === 1) setInternoEscolhido(data[0].id)
      })
  }, [])

  // Relê o PDF guardado e compara com o que já está alocado. O que sobrar são
  // as linhas sem dono. Não se guarda nada: é sempre calculado do documento,
  // que é a única fonte que não mente sobre o que veio na caixa.
  const procurarSobras = async () => {
    if (!compra.fatura_pdf_url) { alert('Esta compra não tem PDF anexado — sem ele não dá para saber que linhas vieram.'); return }
    setAProcurar(true)
    try {
      const { data: blob, error } = await supabase.storage.from('faturas-fornecedor').download(compra.fatura_pdf_url)
      if (error) throw new Error(error.message)
      const parsed = await parseFaturaPdfFile(blob)
      if (!parsed?.linhas?.length) throw new Error('Não consegui ler linhas de produto deste PDF.')

      // Catálogo por EAN e por nome: o fornecedor escreve os nomes à maneira
      // dele (e em espanhol), o EAN é que é igual dos dois lados.
      const { data: prod } = await lerTudo(() => supabase.from('Produtos').select('id, Nome, EAN'), 'id')
      const { data: int } = await lerTudo(() => supabase.from('produtos_internos').select('produto_nome, preco_custo'), 'produto_nome')
      const custoPorNome = new Map((int || []).filter(r => r.preco_custo != null).map(r => [r.produto_nome, Number(r.preco_custo)]))
      const porEan = new Map((prod || []).filter(p => p.EAN).map(p => [String(p.EAN), p]))
      const porNome = new Map((prod || []).map(p => [norm(p.Nome), p]))

      // Quanto de cada artigo já está alocado a encomendas de clientes
      const alocado = new Map()
      for (const l of links) {
        const k = l.produto_nome
        alocado.set(k, (alocado.get(k) || 0) + Number(l.quantidade_alocada || 0))
      }

      const linhas = []
      for (const l of parsed.linhas) {
        const p = (l.ean && porEan.get(String(l.ean))) || porNome.get(norm(l.nome || '')) || null
        const nomeBD = p?.Nome || null
        const jaAlocado = nomeBD ? (alocado.get(nomeBD) || 0) : 0
        const sobra = Number(l.qty || 0) - jaAlocado
        if (sobra <= 0) continue
        linhas.push({
          ean: l.ean, nomeFactura: l.nome, nomeBD,
          qtd: sobra,
          custo: nomeBD ? custoPorNome.get(nomeBD) ?? null : null,
          custoFactura: Number(l.custo_unit_real || 0),
        })
      }
      setSobras(linhas)
      // Consumo por omissão: é o caso mais comum e é o que não tira nada do
      // catálogo. Esconder um artigo tem de ser um acto, não um esquecimento.
      const d = {}
      for (const l of linhas) d[l.ean || l.nomeBD] = 'consumo'
      setDestino(d)
    } catch (e) {
      alert('Erro: ' + (e?.message || String(e)))
    } finally { setAProcurar(false) }
  }

  const chaveLinha = (l) => l.ean || l.nomeBD

  const criarEncomendaInterna = async () => {
    const utilizaveis = (sobras || []).filter(l => l.nomeBD && l.custo != null)
    if (utilizaveis.length === 0) { alert('Nenhuma das linhas que sobram está no catálogo com custo registado.'); return }
    if (!internoEscolhido) { alert('Escolhe a conta interna.'); return }
    const total = utilizaveis.reduce((s, l) => s + l.qtd * l.custo, 0)
    const perfil = perfisInternos.find(p => p.id === internoEscolhido)
    const nRevenda = utilizaveis.filter(l => destino[chaveLinha(l)] === 'revenda').length
    if (!window.confirm(
      `Criar encomenda interna para ${perfil?.nome || 'conta interna'} com ${utilizaveis.length} artigo(s)?\n\n` +
      `Total ao custo: ${fmtEur(total)}\n\n` +
      (nRevenda > 0
        ? `${nRevenda} artigo(s) marcado(s) para REVENDA ficam reservados para ti e saem do catálogo público.\n` +
          `Os restantes ${utilizaveis.length - nRevenda} continuam à venda para todos.\n\n`
        : `Todos ficam disponíveis no catálogo — nenhum é reservado.\n\n`) +
      `Entra como paga, ao preço de custo, e fica fora dos cálculos de lucro — não é venda.\n` +
      `Fica ligada a esta factura.`
    )) return
    setACriarInterna(true)
    try {
      const { data: enc, error } = await supabase.rpc('criar_encomenda_com_itens', {
        p_encomenda: {
          cliente_id: internoEscolhido,
          cliente_email: perfil?.email || null,
          cliente_nome: perfil?.nome || 'Conta interna',
          total_itens: utilizaveis.length,
          notas: `Artigos da factura ${compra.numero_fatura || ''} que não são de encomendas de clientes.`,
          // Fornecedores de pagamento total recusam a encomenda sem isto — a
          // guarda [PAGAMENTO] da RPC corre para todas, internas incluídas. E
          // faz sentido: uma encomenda interna entra paga por inteiro, nunca a
          // meias.
          tipo_pagamento: '100',
        },
        p_itens: utilizaveis.map(l => ({
          produto_nome: l.nomeBD,
          produto_ean: l.ean || null,
          quantidade: l.qtd,
        })),
      })
      if (error) throw new Error(error.message)

      // Só agora se reserva o que foi marcado para revenda. A RPC não reclama
      // nada nas encomendas internas — a decisão é desta lista, artigo a
      // artigo, e é aqui que se executa.
      //
      // O `.is('Email_Cliente', null)` é obrigatório: sem ele, um artigo que já
      // seja de um cliente passava a ser meu, e ele perdia a exclusividade que
      // comprou sem nada que o dissesse.
      const paraRevenda = utilizaveis.filter(l => destino[chaveLinha(l)] === 'revenda')
      if (paraRevenda.length > 0 && perfil?.email) {
        const { error: errRes } = await supabase.from('Produtos')
          .update({ Email_Cliente: perfil.email, updated_at: new Date().toISOString() })
          .in('Nome', paraRevenda.map(l => l.nomeBD))
          .is('Email_Cliente', null)
        if (errRes) alert('Encomenda criada, mas falhou reservar os artigos de revenda: ' + errRes.message)
      }

      if (enc?.id) await ligarEncomendas([enc.id])
      setSobras(null)
      await carregar()
      onUpdated?.()
    } catch (e) {
      alert('Erro ao criar encomenda interna: ' + (e?.message || String(e)))
    } finally { setACriarInterna(false) }
  }

  const carregar = async () => {
    setLoading(true)
    // Links existentes
    const { data: linkData } = await supabase
      .from('compras_fornecedor_encomendas')
      // Campos de pagamento tambem: sao precisos para o painel de cobertura,
      // que compara o que entrou dos clientes com o que se deve ao fornecedor.
      .select('*, encomenda:encomendas(numero_encomenda, cliente_nome, estado, total_final, total_com_iva, pago_1a_prestacao, pago_2a_prestacao, pago_1a_valor, sinal_escolhido, tipo_pagamento)')
      .eq('compra_id', compra.id)
    setLinks(linkData || [])
    // Itens de encomendas nao canceladas — auto-sugere as que tem fornecedor_fatura_numero == compra.numero_fatura
    const { data: itensData } = await supabase
      .from('encomenda_itens')
      .select('id, encomenda_id, produto_nome, produto_ean, quantidade, preco_unitario, encomendas!inner(numero_encomenda, cliente_nome, estado, fornecedor_fatura_numero)')
      .neq('encomendas.estado', 'cancelado')
    setItensDisponiveis(itensData || [])
    setLoading(false)
  }
  useEffect(() => { carregar() }, [compra.id])

  const totalAlocadoPrevisao = links.reduce((s, l) => s + Number(l.valor_alocado_previsao || (Number(l.custo_unitario_previsao || 0) * Number(l.quantidade_alocada || 0))), 0)
  const totalAlocadoFinal = links.reduce((s, l) => s + Number(l.valor_alocado_final || (Number(l.custo_unitario_final || 0) * Number(l.quantidade_alocada || 0))), 0)
  const valorPrevisao = Number(compra.valor_sem_iva || 0)
  const valorFinal = Number(compra.valor_final_sem_iva || 0)

  // ── Cobertura do pagamento ao fornecedor ───────────────────────────────────
  // Responde a uma pergunta pratica, na ordem em que ela se poe: quanto falta
  // pagar desta factura, quanto ha em caixa vindo destes clientes, e a
  // diferenca entre as duas — o dinheiro que tem de vir de outro lado.
  //
  // A soma e linear — a percentagem do custo total = soma das percentagens de
  // cada encomenda — por isso comparar ao nivel da factura da o mesmo que somar
  // encomenda a encomenda.
  const cobertura = useMemo(() => {
    const ehFinal = compra.fase === 'final' && Number(compra.valor_final_total) > 0
    const baseCusto = ehFinal ? Number(compra.valor_final_total) : Number(compra.valor_total || 0)
    const pctForn = Number(compra.sinal_percentagem ?? 50) || 50

    // O QUE JA SAIU DO BANCO, nao uma percentagem recalculada.
    //
    // A 1.a prestacao da factura 105 pagou-se sobre a previsao: 2.067,86 = 20%
    // de 10.339,30. Quando a factura final veio a 8.920,24, esta caixa refazia
    // "20% de 8.920,24" e dizia 1.784,05 — punha de lado 283,81 EUR que ja
    // tinham saido. Pior: a linha de baixo dizia "sobra depois de pagar
    // 4.143,47" quando na verdade faltavam 2.992,72 para pagar a 2.a.
    let pago = 0
    if (compra.pago_1a_prestacao) {
      pago += compra.pago_1a_valor != null
        ? Number(compra.pago_1a_valor)
        : Math.round((baseCusto * pctForn / 100) * 100) / 100
    }
    if (compra.pago_2a_prestacao) {
      pago += compra.pago_2a_valor != null ? Number(compra.pago_2a_valor) : baseCusto - pago
    }
    const pagoAoFornecedor = Math.round(pago * 100) / 100
    const faltaPagar = Math.round((baseCusto - pagoAoFornecedor) * 100) / 100
    // Enquanto o sinal nao sair, o proximo cheque e so o sinal; depois disso e
    // tudo o que falta. Sao coisas diferentes e a caixa tem de dizer qual e.
    const proximoPagamento = compra.pago_1a_prestacao
      ? faltaPagar
      : Math.round((baseCusto * pctForn / 100) * 100) / 100

    // UMA VEZ POR ENCOMENDA, não por linha.
    //
    // `links` tem uma linha por (encomenda, produto): a encomenda do Ruben, com
    // tres artigos nesta factura, aparece tres vezes. Somava-se o total dela em
    // cada uma e a caixa dizia que havia milhares que não existem.
    let recebido = 0, totalEncomendas = 0, aguardam = 0
    const jaContadas = new Set()
    for (const l of links) {
      const e = l.encomenda
      if (!e || e.estado === 'cancelado') continue
      if (jaContadas.has(l.encomenda_id)) continue
      jaContadas.add(l.encomenda_id)
      const tot = Number(e.total_final ?? e.total_com_iva ?? 0)
      totalEncomendas += tot
      // A cadeia completa, a mesma da Tesouraria: o que entrou > pagamento a
      // 100% > sinal escolhido > metade. Faltava aqui o degrau dos 100% — e a
      // coluna nem vinha na query — por isso uma encomenda paga por inteiro a
      // cabeca, sem pago_1a_valor gravado, contava por metade. E este numero
      // decide se ha dinheiro para pagar ao fornecedor.
      const prim = e.pago_1a_valor != null ? Number(e.pago_1a_valor)
                 : String(e.tipo_pagamento) === '100' ? tot
                 : e.sinal_escolhido != null ? Number(e.sinal_escolhido)
                 : tot / 2
      if (e.pago_2a_prestacao) recebido += tot
      else if (e.pago_1a_prestacao) recebido += prim
      else aguardam += 1
    }
    recebido = Math.round(recebido * 100) / 100
    totalEncomendas = Math.round(totalEncomendas * 100) / 100
    const porReceber = Math.round((totalEncomendas - recebido) * 100) / 100
    const caixa = Math.round((recebido - pagoAoFornecedor) * 100) / 100
    const precisa = Math.round((proximoPagamento - caixa) * 100) / 100
    return {
      ehFinal, baseCusto, pctForn, pagoAoFornecedor, faltaPagar, proximoPagamento,
      recebido, totalEncomendas, porReceber, caixa, precisa, aguardam,
      liquidada: faltaPagar <= 0.005,
    }
  }, [links, compra])

  // ── Desconto por escalão ───────────────────────────────────────────────────
  // Regra do fornecedor: até 199 un → 10%; a partir de 200 un → 12%.
  // O escalao e uma propriedade DA FACTURA (depende do total de unidades), nao
  // do artigo. Mas ha artigos com desconto proprio mais baixo (ex: K18 a 5%) —
  // esses mantem o seu, senao escrevia-se um custo errado com ar de certo.
  // Nada disto e automatico: preenche os campos e a pessoa confirma. A factura
  // do fornecedor e que manda; isto e so a calculadora que se faria a mao.
  // Os escalões saem da ficha do fornecedor DESTA compra.
  //
  // Estavam aqui em três constantes — 200 un, 10%, 12% — que são as condições
  // da Plus Skill. Numa factura do Naypes, que vende ao preço de tabela, o
  // botão oferecia-se a gravar os custos 10% abaixo do que foi pago. Não é um
  // número no ecrã: é um UPDATE ao custo de cada linha, e a partir daí toda a
  // margem daquela factura passava a ser inventada.
  //
  // Sem desconto base na ficha não há botão nenhum: não há nada a calcular.
  const [regrasForn, setRegrasForn] = useState(null)
  useEffect(() => {
    if (!compra.fornecedor_id) { setRegrasForn(null); return }
    supabase.from('fornecedores')
      .select('nome, desconto_base_pct, escalao_unidades, escalao_valor_eur, escalao_pct')
      .eq('id', compra.fornecedor_id).maybeSingle()
      .then(({ data }) => setRegrasForn(data || null))
  }, [compra.fornecedor_id])

  const DESC_BASE = Number(regrasForn?.desconto_base_pct) || 0
  const ESCALAO_UN = Number(regrasForn?.escalao_unidades) || 0
  // Escalão medido em EUROS, para quem o mede assim. A Plus Skill dá +2% acima
  // de 200 unidades; o Bplatz dá +3% acima de 5.000 €. É a mesma ideia contada
  // noutra unidade, e só a primeira existia.
  const ESCALAO_EUR = Number(regrasForn?.escalao_valor_eur) || 0
  const PCT_ESCALAO = Number(regrasForn?.escalao_pct) || 0
  const DESC_ESCALAO = DESC_BASE + PCT_ESCALAO
  // Há fornecedores sem desconto base nenhum que mesmo assim têm escalão — é o
  // caso do Bplatz, que vende a tabela e só dá os 3% acima dos 5.000. A
  // condição era só `DESC_BASE > 0` e escondia-lhes o botão, deixando o
  // desconto por aplicar sem ninguém dar por isso.
  const temEscaloes = DESC_BASE > 0 || PCT_ESCALAO > 0

  const unidadesFactura = links.reduce((s, l) => s + Number(l.quantidade_alocada || 0), 0)
  // Sem IVA: o escalão do fornecedor é sobre mercadoria, como todos os limiares.
  const valorFacturaSemIva = Number(compra.valor_final_sem_iva) > 0
    ? Number(compra.valor_final_sem_iva) : Number(compra.valor_sem_iva || 0)
  const atingeEscalao = (ESCALAO_UN > 0 && unidadesFactura >= ESCALAO_UN)
    || (ESCALAO_EUR > 0 && valorFacturaSemIva >= ESCALAO_EUR)
  const descontoSugerido = atingeEscalao ? DESC_ESCALAO : DESC_BASE
  // Mostra-se na unidade em que o fornecedor mede. Dizer "180 un" a quem
  // negoceia em euros não explica de onde vem — ou não vem — o desconto.
  const medidaEscalao = ESCALAO_EUR > 0
    ? `${valorFacturaSemIva.toFixed(2)} € s/IVA`
    : `${unidadesFactura} un`
  // O escalão é a sugestão, não a lei: o fornecedor pode dar 12% a 190 un por
  // acordo, e foi o que aconteceu. Se o valor ficasse preso às unidades,
  // gravava-se um custo 180 EUR acima do que a factura diz.
  const [descontoDaFactura, setDescontoDaFactura] = useState(descontoSugerido)
  useEffect(() => { setDescontoDaFactura(descontoSugerido) }, [descontoSugerido])

  const [previewEscalao, setPreviewEscalao] = useState(null)
  const [aplicandoEscalao, setAplicandoEscalao] = useState(false)
  const [candidatas, setCandidatas] = useState(null)          // encomendas à espera de factura
  const [selCandidatas, setSelCandidatas] = useState(new Set())
  const [ligando, setLigando] = useState(false)
  const [internosIds, setInternosIds] = useState(new Set())

  useEffect(() => {
    supabase.from('perfis').select('id').eq('cliente_interno', true)
      .then(({ data }) => setInternosIds(new Set((data || []).map(p => p.id))))
  }, [])
  const [distribuirResto, setDistribuirResto] = useState(true)

  // O cabecalho da factura e a verdade; as linhas tem de somar exactamente isso.
  // O desconto explica a maior parte do custo, mas sobra sempre um resto —
  // portes (0,75/un), arredondamentos, taxas inesperadas. Esse resto e
  // distribuido pelas UNIDADES, que e a atribuicao correcta para os portes e
  // continua a ser razoavel para o que la venha ter.
  //
  // NAO se forca a soma ao centimo. O custo por unidade tem 2 casas decimais,
  // por isso mexer numa linha move o total em qtd x 1 centimo — nunca menos.
  // Tentei encostar a sobra a linha maior e o resultado era pior: umas vezes nao
  // fechava, outras passava para o outro lado. Fica o resto a vista, que sao
  // centimos de arredondamento e nao um erro por descobrir.
  const escalaoCalculado = useMemo(() => {
    if (!previewEscalao) return null
    // Desconto por linha: o do artigo se tiver um, senão o da factura.
    const base = previewEscalao.map(l => {
      const desc = l.descProprio != null ? l.descProprio : descontoDaFactura
      return {
        ...l, desc,
        excepcao: l.descProprio != null,
        origem: l.descProprio != null ? 'desconto próprio do artigo' : `${descontoDaFactura}% da factura`,
        custoFinal: l.bruto != null ? Math.round(l.bruto * (1 - desc / 100) * 100) / 100 : null,
      }
    })
    const validas = base.filter(l => l.custoFinal != null)
    const somaBase = validas.reduce((s, l) => s + l.custoFinal * l.qtd, 0)
    const unidades = validas.reduce((s, l) => s + l.qtd, 0)
    const alvo = valorFinal > 0 ? valorFinal : valorPrevisao
    const temAlvo = alvo > 0 && unidades > 0
    const resto = temAlvo ? alvo - somaBase : 0
    const porUnidade = (distribuirResto && temAlvo) ? resto / unidades : 0

    const linhas = base.map(l => l.custoFinal == null ? { ...l, custoAplicar: null } : {
      ...l,
      custoAplicar: Math.round((l.custoFinal + porUnidade) * 100) / 100,
    })
    const total = linhas.reduce((s, l) => s + (l.custoAplicar != null ? l.custoAplicar * l.qtd : 0), 0)

    // Tolerancia proporcional as unidades, nao um valor fixo.
    //
    // Cada custo/un e gravado com 2 casas decimais, por isso cada linha pode
    // afastar-se ate meio centimo por unidade. Numa factura de 220 unidades sao
    // 1,10 EUR de desvio possivel so por arredondamento. Foi o que aconteceu na
    // 105: as seis linhas arredondaram todas para cima e a soma ficou 0,76 EUR
    // acima da factura. Com o limiar fixo em 0,50 EUR, o ecra dizia "confere as
    // linhas" numa conta que estava certa — e o aviso que nao serve para nada e
    // o aviso que se ignora quando ha mesmo um erro.
    const tolerancia = Math.round((unidades * 0.005 + 0.01) * 100) / 100
    return { linhas, somaBase, unidades, alvo, temAlvo, resto, porUnidade, total, tolerancia, diff: temAlvo ? total - alvo : null }
  }, [previewEscalao, distribuirResto, valorFinal, valorPrevisao, descontoDaFactura])

  const prepararEscalao = async () => {
    if (links.length === 0) return
    const nomes = [...new Set(links.map(l => l.produto_nome).filter(Boolean))]
    const [precosRes, descontosRes] = await Promise.all([
      supabase.from('produtos_internos').select('produto_nome, preco_fornecedor').in('produto_nome', nomes),
      supabase.from('Produtos').select('Nome, desconto_supplier_pct').in('Nome', nomes),
    ])
    const preco = new Map((precosRes.data || []).map(r => [r.produto_nome, Number(r.preco_fornecedor)]))
    const descProprio = new Map((descontosRes.data || [])
      .filter(r => r.desconto_supplier_pct != null)
      .map(r => [r.Nome, Number(r.desconto_supplier_pct)]))

    // Guarda-se só o que vem da base de dados. O desconto e o custo são
    // calculados no memo, para a percentagem poder mudar sem ir buscar tudo
    // outra vez a cada tecla.
    const linhas = links.map(l => ({
      id: l.id,
      produto: l.produto_nome,
      qtd: Number(l.quantidade_alocada || 0),
      bruto: preco.get(l.produto_nome) ?? null,
      descProprio: descProprio.get(l.produto_nome) ?? null,
      actual: l.custo_unitario_final != null ? Number(l.custo_unitario_final) : null,
    }))
    setPreviewEscalao(linhas)
  }

  const aplicarEscalao = async () => {
    if (!escalaoCalculado) return
    const validas = escalaoCalculado.linhas.filter(l => l.custoAplicar != null)
    if (validas.length === 0) { alert('Nenhuma linha tem preço de fornecedor para calcular.'); return }
    setAplicandoEscalao(true)
    try {
      for (const l of validas) {
        await supabase.from('compras_fornecedor_encomendas').update({
          custo_unitario_final: l.custoAplicar,
          custo_unitario: l.custoAplicar,
          valor_alocado_final: Number((l.custoAplicar * l.qtd).toFixed(2)),
          valor_alocado: Number((l.custoAplicar * l.qtd).toFixed(2)),
        }).eq('id', l.id)
      }
      setPreviewEscalao(null)
      await carregar(); await onUpdated()
    } finally { setAplicandoEscalao(false) }
  }

  const autoLinkar = async () => {
    if (!compra.numero_fatura) { alert('Esta compra não tem nº de factura definido.'); return }
    // 2 queries (mais robusto que join filter)
    const { data: encs, error: encsErr } = await supabase
      .from('encomendas')
      .select('id')
      .eq('fornecedor_fatura_numero', compra.numero_fatura)
      .neq('estado', 'cancelado')
    if (encsErr) { alert('Erro: ' + encsErr.message); return }
    const encIds = (encs || []).map(e => e.id)

    // Nenhuma encomenda tem esta factura atribuida — o caso normal logo depois
    // de criar a compra. Em vez de desistir com uma mensagem tecnica, procura as
    // que estao a espera de factura e deixa escolher quais ligar. Nao se liga
    // tudo as cegas: podem estar la encomendas que pertencem a outra factura.
    if (encIds.length === 0) {
      await procurarCandidatas()
      return
    }
    await ligarEncomendas(encIds)
  }

  // Procura as encomendas que ainda esperam factura. Os filtros vivem TODOS no
  // servidor de proposito: a primeira versao filtrava as historicas no browser
  // mas nao trazia a coluna, por isso `e.historica` era undefined e o filtro nao
  // filtrava nada — a Pre/site aparecia na lista. Filtro que depende de alguem
  // se lembrar de pedir a coluna e filtro que volta a falhar.
  const procurarCandidatas = async () => {
    let q = supabase
      .from('encomendas')
      .select('id, numero_encomenda, cliente_nome, cliente_id, total_final, encomendado_fornecedor_em, fornecedor_id')
      .is('fornecedor_fatura_numero', null)
      .eq('historica', false)
      .neq('estado', 'cancelado')
      .neq('estado', 'concluido')
      .or('pago_1a_prestacao.eq.true,promovido_pedido_em.not.is.null')
      .order('numero_encomenda')
    // SÓ encomendas DESTE fornecedor. Sem isto, ao procurar candidatas para uma
    // factura do Naypes apareciam também as do Plus Skill — e vinham todas
    // pré-seleccionadas. Bastava aceitar para ficarem encomendas de um
    // fornecedor penduradas na factura de outro: o custo real alocado a artigos
    // que não vieram nela, e o lucro por factura a comparar coisas diferentes.
    //
    // Encomendas antigas sem fornecedor gravado continuam a aparecer, porque
    // antes do Naypes era tudo do mesmo — escondê-las tirava-as do alcance.
    if (compra.fornecedor_id) {
      q = q.or(`fornecedor_id.eq.${compra.fornecedor_id},fornecedor_id.is.null`)
    }
    const { data: cand, error } = await q
    if (error) { alert('Erro: ' + error.message); return }

    // Fora as que ja pertencem a outra compra
    const { data: jaLigadas } = await supabase
      .from('compras_fornecedor_encomendas').select('encomenda_id')
    const ocupadas = new Set((jaLigadas || []).map(l => l.encomenda_id))
    const livres = (cand || []).filter(e => !ocupadas.has(e.id))

    if (livres.length === 0) {
      alert('Nenhuma encomenda à espera de factura.\n\nOu já estão todas ligadas a uma compra, ou ainda não foram pagas nem promovidas.')
      return
    }

    const { data: itens } = await supabase
      .from('encomenda_itens').select('encomenda_id, quantidade')
      .in('encomenda_id', livres.map(e => e.id))
    const unPorEnc = new Map()
    for (const it of (itens || [])) {
      unPorEnc.set(it.encomenda_id, (unPorEnc.get(it.encomenda_id) || 0) + Number(it.quantidade || 0))
    }

    setCandidatas(livres.map(e => ({
      id: e.id,
      numero: e.numero_encomenda,
      cliente: e.cliente_nome,
      interno: internosIds.has(e.cliente_id),
      un: unPorEnc.get(e.id) || 0,
      valor: Number(e.total_final || 0),
    })))
    setSelCandidatas(new Set(livres.map(e => e.id)))
  }

  const ligarEncomendas = async (encIds) => {
    const { data: itensMatch, error: itensErr } = await supabase
      .from('encomenda_itens')
      .select('encomenda_id, produto_id, produto_nome, quantidade')
      .in('encomenda_id', encIds)
    if (itensErr) { alert('Erro: ' + itensErr.message); return }
    if (!itensMatch || itensMatch.length === 0) {
      alert('Encomendas encontradas mas sem itens.')
      return
    }
    const linkadas = new Set(links.map(l => `${l.encomenda_id}|${l.produto_nome}`))
    const novos = itensMatch.filter(it => !linkadas.has(`${it.encomenda_id}|${it.produto_nome}`))
    if (novos.length === 0) {
      alert('Todas as encomendas que correspondem à factura já estão linkadas.')
      return
    }
    // Custo previsao = preco_custo do catalogo por produto (NUNCA media uniforme)
    const nomesUnicos = [...new Set(novos.map(it => it.produto_nome).filter(Boolean))]
    const { data: catCustos } = await supabase
      .from('produtos_internos')
      .select('produto_nome, preco_custo')
      .in('produto_nome', nomesUnicos)
    const custoPorNome = new Map((catCustos || []).map(r => [r.produto_nome, Number(r.preco_custo)]))

    // Produtos sem custo gravavam null em silencio, e o lucro dessas encomendas
    // aparecia inflacionado em toda a app (a Analise Factura chegou a mostrar 98%
    // de margem numa factura em que faltavam 5.913 EUR de custo). Avisar antes.
    const semCusto = [...new Set(novos.map(it => it.produto_nome))]
      .filter(n => !custoPorNome.has(n) || !(Number(custoPorNome.get(n)) > 0))
    if (semCusto.length > 0) {
      const lista = semCusto.slice(0, 8).join('\n· ')
      const resto = semCusto.length > 8 ? `\n… e mais ${semCusto.length - 8}` : ''
      const seguir = confirm(
        `${semCusto.length} produto(s) SEM CUSTO registado:\n\n· ${lista}${resto}\n\n` +
        `Se continuares, estas linhas ficam sem custo e o lucro desta factura ` +
        `aparece inflacionado no Lucro e na Análise Factura.\n\n` +
        `Recomendado: cancelar, preencher o preço de custo destes produtos, e ligar depois.\n\n` +
        `Continuar mesmo assim?`
      )
      if (!seguir) return
    }

    const rows = novos.map(it => {
      const custoUn = custoPorNome.has(it.produto_nome) ? custoPorNome.get(it.produto_nome) : null
      const custoUnNum = custoUn != null ? Number(Number(custoUn).toFixed(4)) : null
      const valorAloc = custoUnNum != null ? Number((custoUnNum * Number(it.quantidade)).toFixed(2)) : null
      return {
        compra_id: compra.id,
        encomenda_id: it.encomenda_id,
        produto_id: it.produto_id,
        produto_nome: it.produto_nome,
        quantidade_alocada: it.quantidade,
        custo_unitario_previsao: custoUnNum,
        custo_unitario: custoUnNum,
        valor_alocado_previsao: valorAloc,
        valor_alocado: valorAloc,
      }
    })
    const { error } = await supabase.from('compras_fornecedor_encomendas').insert(rows)
    if (error) { alert('Erro: ' + error.message); return }

    // Gravar a factura nas encomendas que ainda não a tinham. Sem isto ficavam
    // ligadas à compra mas sem saber a que factura pertencem, e o resto da app
    // (cores por factura, arquivo, análise) não as agrupava.
    const semFactura = encIds.filter(id => id)
    if (semFactura.length > 0 && compra.numero_fatura) {
      await supabase.from('encomendas')
        .update({ fornecedor_fatura_numero: compra.numero_fatura })
        .in('id', semFactura)
        .is('fornecedor_fatura_numero', null)
    }

    // Auto-aplicar transitos nas encomendas que ainda nao tinham aplicado.
    // Evita incoerencias tipo "Sandro nao tem transito mas Catia tem" porque
    // Catia foi linkada antes de uma rotacao e Sandro depois.
    const encIdsNovos = [...new Set(novos.map(it => it.encomenda_id))]
    const { data: pendentes } = await supabase
      .from('encomendas')
      .select('id')
      .in('id', encIdsNovos)
      .or('transitos_aplicados.is.null,transitos_aplicados.eq.false')
    let aplicadosTransito = 0
    for (const e of (pendentes || [])) {
      try {
        const { error: rpcErr } = await supabase.rpc('reaplicar_transitos_encomenda', { p_enc_id: e.id })
        if (!rpcErr) aplicadosTransito++
      } catch { /* continua */ }
    }

    await carregar()
    await onUpdated()
    alert(`${rows.length} item(s) auto-linkado(s). ${aplicadosTransito} encomenda(s) com trânsitos auto-aplicados.`)
  }

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '4px', padding: '12px 14px', background: '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '6px', flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: C.text, textTransform: 'uppercase' }}>
          📦 Encomendas cobertas por esta factura ({links.length})
        </p>
        {/* Painel de cobertura — ver comentario em `cobertura` acima. */}
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {links.length > 0 && temEscaloes && (
            <button onClick={prepararEscalao}
              title={`${medidaEscalao} nesta factura → desconto de ${descontoDaFactura}%. Preenche o custo final de todas as linhas; artigos com desconto próprio mantêm o seu. Mostra tudo antes de gravar.`}
              style={{ background: atingeEscalao ? '#107c10' : C.surface, color: atingeEscalao ? '#fff' : C.text, border: `1px solid ${atingeEscalao ? '#107c10' : C.border}`, borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
              % Custos a {descontoDaFactura}% ({medidaEscalao})
            </button>
          )}
          <button onClick={autoLinkar} title="Liga a esta factura as encomendas que lhe pertencem. Se nenhuma tiver ainda a factura atribuída, mostra as que estão à espera de factura e pergunta se queres ligá-las."
            style={{ background: C.success, color: '#fff', border: 'none', borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
            ⚡ Ligar encomendas
          </button>
          <button onClick={() => setShowAdd(s => !s)}
            style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: '3px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
            {showAdd ? 'Fechar' : '+ Manual'}
          </button>
          {compra.fatura_pdf_url && (
            <button onClick={procurarSobras} disabled={aProcurar}
              title="Relê o PDF e mostra as linhas da factura que não pertencem a encomenda de cliente nenhuma — as que são para consumo ou revenda própria."
              style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '4px 10px', cursor: aProcurar ? 'wait' : 'pointer', fontSize: '11px', fontWeight: '700' }}>
              {aProcurar ? 'A ler PDF…' : '⌂ Linhas que sobram'}
            </button>
          )}
        </div>
      </div>

      {/* ── O QUE NÃO É DE CLIENTE NENHUM ───────────────────────────────────
          Vai para a conta interna, ao custo. Não é venda: fica fora do lucro.
          Se depois for vendido num marketplace, regista-se lá — o custo já cá
          está, e é isso que evita ter de decidir na altura da compra o que é
          para consumo e o que é para revender. */}
      {sobras !== null && (
        <div style={{ margin: '0 0 12px', padding: '12px 14px', background: '#f5f3ff', border: `1px solid #c7b9f4`, borderRadius: '4px' }}>
          {sobras.length === 0 ? (
            <p style={{ margin: 0, fontSize: '12px', color: C.success, fontWeight: '600' }}>
              ✓ Nada sobra — todas as linhas desta factura estão em encomendas de clientes.
            </p>
          ) : (() => {
            const utilizaveis = sobras.filter(l => l.nomeBD && l.custo != null)
            const foraDoCatalogo = sobras.filter(l => !l.nomeBD || l.custo == null)
            const total = utilizaveis.reduce((s, l) => s + l.qtd * l.custo, 0)
            return (
              <>
                <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '700', color: '#5b21b6' }}>
                  {sobras.length} linha(s) desta factura não são de encomenda nenhuma
                </p>
                <p style={{ margin: '0 0 8px', fontSize: '11px', color: C.textMuted, lineHeight: 1.5 }}>
                  Vão todas para a conta interna <strong>ao custo</strong> — não é venda, não gera lucro e sai
                  dos cálculos de margem. Escolhe artigo a artigo o que acontece ao catálogo:{' '}
                  <strong style={{ color: C.success }}>consumo</strong> deixa-o à venda para todos,{' '}
                  <strong style={{ color: '#92400e' }}>revenda</strong> reserva-o para ti e tira-o do catálogo público.
                </p>
                {/* Por artigo: consumo deixa-o no catálogo de toda a gente,
                    revenda reserva-o para mim e tira-o de lá. O predefinido é
                    consumo — esconder um artigo tem de ser um acto, não um
                    esquecimento. */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px', maxHeight: '220px', overflowY: 'auto' }}>
                  {utilizaveis.map((l, i) => {
                    const k = chaveLinha(l)
                    const d = destino[k] || 'consumo'
                    const btn = (valor, texto, titulo) => (
                      <button onClick={() => setDestino(p => ({ ...p, [k]: valor }))} title={titulo}
                        style={{
                          padding: '2px 8px', fontSize: '10px', fontWeight: '700', cursor: 'pointer',
                          border: `1px solid ${d === valor ? (valor === 'revenda' ? C.warning : C.success) : C.border}`,
                          background: d === valor ? (valor === 'revenda' ? '#fff8e1' : '#e8f5e9') : C.surface,
                          color: d === valor ? (valor === 'revenda' ? '#92400e' : C.success) : C.textMuted,
                          borderRadius: '10px', whiteSpace: 'nowrap',
                        }}>{texto}</button>
                    )
                    return (
                      <div key={`s${i}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '11px', color: C.text }}>
                        <span style={{ minWidth: 0 }}>
                          • {l.nomeBD} — {l.qtd} un × {fmtEur(l.custo)} = <strong>{fmtEur(l.qtd * l.custo)}</strong>
                        </span>
                        <span style={{ display: 'inline-flex', gap: '4px', flexShrink: 0 }}>
                          {btn('consumo', 'consumo', 'Para consumo. O artigo continua disponível a todos os clientes.')}
                          {btn('revenda', 'revenda', 'Para eu vender. O artigo fica reservado para mim e sai do catálogo público.')}
                        </span>
                      </div>
                    )
                  })}
                  {foraDoCatalogo.map((l, i) => (
                    <div key={`f${i}`} style={{ fontSize: '11px', color: C.danger }}>
                      ✕ {l.nomeFactura} — {l.qtd} un · {l.nomeBD ? 'sem custo registado' : 'não está no catálogo'} — fica de fora
                    </div>
                  ))}
                </div>
                {utilizaveis.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {perfisInternos.length > 1 && (
                      <select value={internoEscolhido} onChange={e => setInternoEscolhido(e.target.value)}
                        style={{ height: '30px', padding: '0 8px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }}>
                        <option value="">Escolhe a conta interna…</option>
                        {perfisInternos.map(p => <option key={p.id} value={p.id}>{p.nome || p.email}</option>)}
                      </select>
                    )}
                    <button onClick={criarEncomendaInterna} disabled={aCriarInterna || !internoEscolhido}
                      style={{ height: '30px', padding: '0 12px', border: 'none', borderRadius: '3px', background: internoEscolhido ? '#5b21b6' : C.borderStrong, color: '#fff', cursor: aCriarInterna || !internoEscolhido ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
                      {aCriarInterna ? 'A criar…' : `Criar encomenda interna — ${fmtEur(total)}`}
                    </button>
                    <button onClick={() => setSobras(null)}
                      style={{ height: '30px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '3px', background: C.surface, color: C.textMuted, cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
                      Fechar
                    </button>
                  </div>
                )}
                {perfisInternos.length === 0 && (
                  <p style={{ margin: '6px 0 0', fontSize: '11px', color: C.danger, fontWeight: '600' }}>
                    Não há nenhum perfil marcado como <strong>cliente interno</strong>. Marca um em Utilizadores primeiro.
                  </p>
                )}
              </>
            )
          })()}
        </div>
      )}

      {/* ── COBERTURA DO PAGAMENTO AO FORNECEDOR ────────────────────────────
          Tres blocos na ordem em que a pergunta se poe: o que falta pagar, o
          que ha em caixa, e a diferenca em destaque. Antes eram tres linhas
          soltas — uma percentagem recalculada, um recebido, e uma "sobra" que
          nao correspondia a cheque nenhum. */}
      {links.length > 0 && cobertura.baseCusto > 0 && (() => {
        const falta = !cobertura.liquidada && cobertura.precisa > 0.005
        const cor = falta ? '#92400e' : C.success
        const sep = `1px solid ${falta ? `${C.warning}55` : `${C.success}44`}`
        const rot = { padding: '2px 0', color: C.textMuted, fontSize: '11.5px' }
        const num = { padding: '2px 0', textAlign: 'right', fontFamily: 'monospace', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }
        const rotT = { ...rot, padding: '5px 0 1px', fontWeight: '700', color: C.text, borderTop: sep }
        const numT = { ...num, padding: '5px 0 1px', fontSize: '13.5px', fontWeight: '800', borderTop: sep }
        const dm = (d) => d ? `${String(d).slice(8, 10)}/${String(d).slice(5, 7)}` : ''
        return (
          <div style={{
            marginBottom: '10px', padding: '10px 12px', borderRadius: '4px',
            background: falta ? '#fff8e1' : '#e8f5e9',
            border: `1px solid ${falta ? `${C.warning}66` : `${C.success}55`}`,
          }}>
            <p style={{ margin: '0 0 8px', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', color: cor }}>
              Cobertura do pagamento ao fornecedor
            </p>

            {/* 1 — O QUE SAI */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={rot}>Factura {cobertura.ehFinal ? '(final)' : '(previsão)'}</td>
                  <td style={{ ...num, color: C.text }}>{fmt2(cobertura.baseCusto)} €</td>
                </tr>
                <tr>
                  <td style={rot}>
                    Já pago ao fornecedor
                    {compra.pago_1a_prestacao && compra.pago_1a_data && (
                      <span style={{ fontSize: '10px' }}> · 1.ª a {dm(compra.pago_1a_data)}</span>
                    )}
                    {compra.pago_2a_prestacao && compra.pago_2a_data && (
                      <span style={{ fontSize: '10px' }}> + 2.ª a {dm(compra.pago_2a_data)}</span>
                    )}
                  </td>
                  <td style={{ ...num, color: C.textMuted }}>− {fmt2(cobertura.pagoAoFornecedor)} €</td>
                </tr>
                <tr>
                  <td style={rotT}>{cobertura.liquidada ? 'Factura liquidada' : 'Falta pagar'}</td>
                  <td style={{ ...numT, color: cobertura.liquidada ? C.success : cor }}>{fmt2(cobertura.faltaPagar)} €</td>
                </tr>
              </tbody>
            </table>

            {/* 2 — O QUE ENTROU */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '9px' }}>
              <tbody>
                <tr>
                  <td style={rot}>
                    Recebido dos clientes
                    {cobertura.aguardam > 0 && (
                      <span style={{ color: C.warning, fontWeight: '600' }}> · {cobertura.aguardam} sem pagar</span>
                    )}
                  </td>
                  <td style={{ ...num, color: C.success }}>{fmt2(cobertura.recebido)} €</td>
                </tr>
                <tr>
                  <td style={rot}>Menos o que já saiu daí</td>
                  <td style={{ ...num, color: C.textMuted }}>− {fmt2(cobertura.pagoAoFornecedor)} €</td>
                </tr>
                <tr>
                  <td style={rotT}>Tens em caixa</td>
                  <td style={{ ...numT, color: C.text }}>{fmt2(cobertura.caixa)} €</td>
                </tr>
              </tbody>
            </table>

            {/* 3 — A DIFERENCA, que e a resposta */}
            {!cobertura.liquidada && (
              <div style={{
                marginTop: '9px', padding: '8px 10px', borderRadius: '3px',
                background: falta ? '#fef3c7' : '#dcfce7',
                border: `1px solid ${falta ? C.warning : C.success}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
              }}>
                <span style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.3px', color: cor }}>
                  {falta ? '⚠ Tens de ir buscar' : 'Chega — sobra'}
                </span>
                <span style={{ fontSize: '16px', fontWeight: '800', fontFamily: 'monospace', color: cor, whiteSpace: 'nowrap' }}>
                  {fmt2(Math.abs(cobertura.precisa))} €
                </span>
              </div>
            )}

            <p style={{ margin: '7px 0 0', fontSize: '10.5px', color: C.textMuted, lineHeight: 1.45 }}>
              {!cobertura.liquidada && Math.abs(cobertura.proximoPagamento - cobertura.faltaPagar) > 0.005 && (
                <>Próximo cheque: <strong>{fmt2(cobertura.proximoPagamento)} €</strong> ({cobertura.pctForn}% à cabeça). </>
              )}
              Encomendas ligadas: {fmt2(cobertura.totalEncomendas)} € · ainda por receber <strong>{fmt2(cobertura.porReceber)} €</strong>.
            </p>
          </div>
        )
      })()}

      {/* Escolher que encomendas ligar. Não se liga tudo às cegas: pode haver
          encomendas à espera que pertençam a outra factura. */}
      {candidatas && (() => {
        const sel = candidatas.filter(c => selCandidatas.has(c.id))
        const un = sel.reduce((s, c) => s + c.un, 0)
        const valor = sel.reduce((s, c) => s + c.valor, 0)
        const alternar = (id) => setSelCandidatas(p => {
          const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n
        })
        return (
          <div style={{ border: `2px solid ${C.primary}`, borderRadius: 4, padding: 12, marginBottom: 10, background: C.surface }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 12, color: C.text }}>
                {candidatas.length} encomenda(s) à espera de factura — escolhe as desta:
              </strong>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setSelCandidatas(new Set(candidatas.map(c => c.id)))}
                  style={{ padding: '3px 9px', border: `1px solid ${C.border}`, borderRadius: 3, background: C.surface, cursor: 'pointer', fontSize: 10.5, fontWeight: 600 }}>Todas</button>
                <button onClick={() => setSelCandidatas(new Set())}
                  style={{ padding: '3px 9px', border: `1px solid ${C.border}`, borderRadius: 3, background: C.surface, cursor: 'pointer', fontSize: 10.5, fontWeight: 600 }}>Nenhuma</button>
              </div>
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
                <tbody>
                  {candidatas.map(c => (
                    <tr key={c.id} onClick={() => alternar(c.id)}
                      style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer', background: selCandidatas.has(c.id) ? '#f0f9ff' : 'transparent' }}>
                      <td style={{ padding: '5px 6px', width: 26 }}>
                        <input type="checkbox" readOnly checked={selCandidatas.has(c.id)} style={{ cursor: 'pointer', accentColor: C.primary }} />
                      </td>
                      <td style={{ padding: '5px 6px', fontWeight: 700, color: C.primary, whiteSpace: 'nowrap' }}>{c.numero}</td>
                      <td style={{ padding: '5px 6px', color: C.text }}>
                        {c.cliente}
                        {c.interno && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#5b21b6' }}>INTERNO</span>}
                      </td>
                      <td style={{ padding: '5px 6px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>{c.un} un</td>
                      <td style={{ padding: '5px 6px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt2(c.valor)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 8, padding: '7px 9px', background: C.surfaceAlt, borderRadius: 3, fontSize: 11.5, fontWeight: 700, color: C.text }}>
              {sel.length} seleccionada(s) · {un} un · {fmt2(valor)} €
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button disabled={sel.length === 0 || ligando}
                onClick={async () => {
                  setLigando(true)
                  try { await ligarEncomendas([...selCandidatas]); setCandidatas(null) }
                  finally { setLigando(false) }
                }}
                style={{ background: sel.length === 0 ? C.border : C.primary, color: '#fff', border: 'none', borderRadius: 3, padding: '6px 14px', cursor: sel.length === 0 ? 'not-allowed' : 'pointer', fontSize: 11.5, fontWeight: 700 }}>
                {ligando ? 'A ligar…' : `Ligar à factura ${compra.numero_fatura}`}
              </button>
              <button onClick={() => setCandidatas(null)} disabled={ligando}
                style={{ background: C.surface, color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 3, padding: '6px 12px', cursor: 'pointer', fontSize: 11.5, fontWeight: 600 }}>
                Cancelar
              </button>
            </div>
          </div>
        )
      })()}

      {/* Pré-visualização do desconto de escalão — mostra o que vai escrever antes de escrever */}
      {previewEscalao && escalaoCalculado && (() => {
        const { linhas, somaBase, unidades, alvo, temAlvo, resto, porUnidade, total, tolerancia, diff } = escalaoCalculado
        const semPreco = linhas.filter(l => l.custoAplicar == null).length
        return (
          <div style={{ border: `2px solid ${C.primary}`, borderRadius: '4px', padding: '12px', marginBottom: '10px', background: C.surface }}>
            <p style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '700', color: C.text }}>
              Vai escrever o custo final destas {linhas.length} linhas — confere antes:
            </p>
            {/* O que faz as linhas somarem exactamente o total da factura */}
            {/* Percentagem editável: o escalão sugere, mas quem manda é a factura */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, padding: '8px 10px', background: C.surfaceAlt, borderRadius: 3, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>Desconto a aplicar</span>
              <input type="number" min="0" max="100" step="0.5" value={descontoDaFactura}
                onChange={e => setDescontoDaFactura(Number(e.target.value) || 0)}
                style={{ width: 62, padding: '3px 6px', border: `1px solid ${C.primary}`, borderRadius: 3, fontSize: 12, fontWeight: 700, textAlign: 'right' }} />
              <span style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>%</span>
              <span style={{ fontSize: 10.5, color: C.textMuted }}>
                {medidaEscalao} → o escalão sugere {descontoSugerido}%
                {descontoDaFactura !== descontoSugerido && (
                  <strong style={{ color: '#92400e' }}> · a usar {descontoDaFactura}% por acordo com o fornecedor</strong>
                )}
                {' '}· artigos com desconto próprio mantêm o seu
              </span>
            </div>
            {temAlvo && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8, fontSize: 11, color: C.text, cursor: 'pointer', background: C.surfaceAlt, padding: '7px 9px', borderRadius: 3 }}>
                <input type="checkbox" checked={distribuirResto} onChange={e => setDistribuirResto(e.target.checked)} style={{ cursor: 'pointer', accentColor: C.primary, marginTop: 1 }} />
                <span>
                  <strong>Distribuir o resto pelas unidades</strong> — para as linhas somarem exactamente o total da factura.
                  <div style={{ color: C.textMuted, marginTop: 2 }}>
                    Desconto dá {fmt2(somaBase)} €, factura diz {fmt2(alvo)} € → sobram <strong style={{ color: C.text }}>{fmt2(resto)} €</strong> sobre {unidades} un
                    {' '}= <strong style={{ color: C.text }}>{porUnidade ? fmt2(porUnidade) : '0.00'} €/un</strong> (portes e afins)
                  </div>
                </span>
              </label>
            )}
            <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <tbody>
                  {linhas.map(l => (
                    <tr key={l.id} style={{ borderTop: `1px solid ${C.border}`, background: l.excepcao ? '#fff4ce' : 'transparent' }}>
                      <td style={{ padding: '4px 6px', color: C.text }}>
                        {l.produto}
                        {l.excepcao && <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 800, color: '#92400e' }}>EXCEPÇÃO</span>}
                        <div style={{ fontSize: 9, color: C.textMuted }}>{l.origem}</div>
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>{l.qtd} un</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {l.bruto != null ? `${fmt2(l.bruto)} € −${l.desc}%` : <span style={{ color: C.danger }}>sem preço</span>}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: C.text, whiteSpace: 'nowrap' }}>
                        {l.custoAplicar != null ? `${fmt2(l.custoAplicar)} €` : '—'}
                        {l.custoAplicar != null && l.custoAplicar !== l.custoFinal && (
                          <div style={{ fontSize: 9, color: C.textMuted, fontWeight: 400 }}>
                            {fmt2(l.custoFinal)} + {fmt2(l.custoAplicar - l.custoFinal)}{l.ajustado ? ' *' : ''}
                          </div>
                        )}
                        {l.actual != null && l.actual !== l.custoAplicar && (
                          <div style={{ fontSize: 9, color: C.textMuted, fontWeight: 400 }}>era {fmt2(l.actual)} €</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Confronto com a factura: é isto que diz se os números estão bons */}
            {(() => {
              const bate = diff != null && Math.abs(diff) <= tolerancia
              return (
                <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 3, fontSize: 11,
                  background: diff == null ? C.surfaceAlt : bate ? '#dff6dd' : '#fff4ce',
                  color: diff == null ? C.textMuted : bate ? '#107c10' : '#92400e', fontWeight: 600 }}>
                  Soma das linhas <strong>{fmt2(total)} €</strong>
                  {temAlvo ? <> · factura <strong>{fmt2(alvo)} €</strong> · diferença <strong>{fmt2(diff)} €</strong>
                    {bate
                      ? ` ✓ bate certo (arredondamento de ${unidades} un — tolerância ${fmt2(tolerancia)} €)`
                      : ` — acima da tolerância de ${fmt2(tolerancia)} €: confere as linhas ou o valor da factura`}</>
                    : <> · sem valor de factura lançado para comparar</>}
                  {semPreco > 0 && <div style={{ marginTop: 4, color: C.danger }}>⚠ {semPreco} linha(s) sem preço de fornecedor — ficam por preencher.</div>}
                </div>
              )
            })()}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={aplicarEscalao} disabled={aplicandoEscalao}
                style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 3, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                {aplicandoEscalao ? 'A gravar…' : 'Confirmar e gravar'}
              </button>
              <button onClick={() => setPreviewEscalao(null)} disabled={aplicandoEscalao}
                style={{ background: C.surface, color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 3, padding: '5px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                Cancelar
              </button>
            </div>
          </div>
        )
      })()}

      {loading ? <p style={{ fontSize: '12px', color: C.textMuted }}>A carregar...</p> : (
        <>
          {links.length === 0 ? (
            <p style={{ margin: 0, fontSize: '12px', color: C.textMuted, fontStyle: 'italic' }}>
              Nenhuma encomenda linkada ainda. Adiciona as encomendas cobertas por esta factura para que o lucro real seja calculado.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: C.surface }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Encomenda · Cliente</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Produto · Qtd</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Custo/un<br/>(Previsão)</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10px', fontWeight: '700', color: C.textMuted, textTransform: 'uppercase' }}>Custo/un<br/>(Final)</th>
                    <th style={{ padding: '6px 8px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {links.map(l => (
                    <LinkRow key={l.id} link={l} isFinal={isFinal} onUpdated={async () => { await carregar(); await onUpdated() }} />
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: `1px solid ${C.border}`, background: C.surface }}>
                    <td colSpan="2" style={{ padding: '6px 8px', fontWeight: '700', color: C.text, fontSize: '11px' }}>Total alocado</td>
                    {/* Depois de os custos finais estarem preenchidos, e a coluna
                        Final que manda — a Previsao passa a ser historico e nao
                        faz sentido continuar a acusa-la como divergente. */}
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', fontSize: '11px',
                      color: totalAlocadoFinal > 0 ? C.textMuted
                           : (Math.abs(totalAlocadoPrevisao - valorPrevisao) < 1 ? C.success : C.warning) }}>
                      {fmt2(totalAlocadoPrevisao)} / {fmt2(valorPrevisao)} €
                    </td>
                    {/* Compara com o valor efectivo da factura: o final se ja existir,
                        senao o de previsao. Antes so comparava com o final e, enquanto
                        a compra estava em previsao, mostrava "—" — ficava-se sem o
                        unico numero que interessa depois de aplicar os custos. */}
                    {(() => {
                      const alvo = valorFinal > 0 ? valorFinal : valorPrevisao
                      const bate = alvo > 0 && Math.abs(totalAlocadoFinal - alvo) < 1
                      return (
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: '700', fontSize: '11px',
                          color: totalAlocadoFinal <= 0 ? C.textMuted : (bate ? C.success : C.warning) }}
                          title={totalAlocadoFinal > 0 && alvo > 0 ? `Diferença: ${fmt2(totalAlocadoFinal - alvo)} €` : ''}>
                          {totalAlocadoFinal > 0
                            ? <>{fmt2(totalAlocadoFinal)} / {fmt2(alvo)} € {bate ? '✓' : ''}</>
                            : '—'}
                        </td>
                      )
                    })()}
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {showAdd && (
            <AdicionarLinkPanel
              compra={compra}
              itens={itensDisponiveis}
              jaLinkados={links}
              onClose={() => setShowAdd(false)}
              onAdded={async () => { await carregar(); await onUpdated(); setShowAdd(false) }}
            />
          )}
        </>
      )}
    </div>
  )
}

function LinkRow({ link, isFinal, onUpdated }) {
  const [editing, setEditing] = useState(false)
  const [custoPrev, setCustoPrev] = useState(link.custo_unitario_previsao || '')
  const [custoFinal, setCustoFinal] = useState(link.custo_unitario_final || '')
  const [confirmIndisp, setConfirmIndisp] = useState(false)
  const [removingIndisp, setRemovingIndisp] = useState(false)

  const guardar = async () => {
    const qty = Number(link.quantidade_alocada || 0)
    const cp = Number(custoPrev) || null
    const cf = Number(custoFinal) || null
    const update = {
      custo_unitario_previsao: cp,
      custo_unitario_final: cf,
      custo_unitario: cf || cp,                              // valor "efectivo"
      valor_alocado_previsao: cp ? Number((cp * qty).toFixed(2)) : null,
      valor_alocado_final: cf ? Number((cf * qty).toFixed(2)) : null,
      valor_alocado: (cf || cp) ? Number(((cf || cp) * qty).toFixed(2)) : null,
    }
    const { error } = await supabase.from('compras_fornecedor_encomendas').update(update).eq('id', link.id)
    if (error) { alert('Erro: ' + error.message); return }
    setEditing(false)
    await onUpdated()
  }
  const remover = async () => {
    if (!window.confirm('Remover esta linha do link?')) return
    const { error } = await supabase.from('compras_fornecedor_encomendas').delete().eq('id', link.id)
    if (error) { alert('Erro: ' + error.message); return }
    await onUpdated()
  }
  const removerPorIndisponivel = async () => {
    setRemovingIndisp(true)
    try {
      const { data, error } = await supabase.rpc('remover_item_indisponivel', { p_cfe_id: link.id })
      if (error) throw error
      setConfirmIndisp(false)
      await onUpdated()
      alert(`Removido com sucesso.\n\nEncomenda ${data.encomenda_numero} (${data.cliente_nome}):\n- Valor removido: ${Number(data.valor_removido_encomenda).toFixed(2)} €\n- Novo total: ${Number(data.novo_total_encomenda).toFixed(2)} €\n\nCompra fornecedor:\n- Reduzida em: ${Number(data.valor_removido_fornecedor).toFixed(2)} €\n- Novo total: ${Number(data.novo_total_compra).toFixed(2)} €\n\nTrânsito revertido: ${data.transito_revertido ? 'Sim' : 'Não aplicável'}`)
    } catch (err) {
      alert('Erro: ' + (err?.message || String(err)))
    } finally {
      setRemovingIndisp(false)
    }
  }

  const valorFornecedor = Number(link.valor_alocado_final ?? link.valor_alocado_previsao ?? link.valor_alocado ?? 0)
  const estadoEnc = (link.encomenda?.estado || '').toLowerCase()
  const enviada = estadoEnc === 'enviado'

  if (confirmIndisp) {
    return (
      <tr style={{ borderTop: `1px solid ${C.warning}`, background: '#fff8e1' }}>
        <td colSpan={5} style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
            <AlertTriangle size={18} color={C.warning} style={{ flexShrink: 0, marginTop: '1px' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: C.text, marginBottom: '6px' }}>
                Remover por indisponibilidade do fornecedor?
              </div>
              <div style={{ fontSize: '12px', color: C.textMuted, lineHeight: '1.6' }}>
                <strong>Produto:</strong> {link.produto_nome}<br/>
                <strong>Cliente:</strong> {link.encomenda?.cliente_nome} ({link.encomenda?.numero_encomenda})<br/>
                <strong>Quantidade:</strong> {link.quantidade_alocada} un · <strong>Valor fornecedor:</strong> {fmt2(valorFornecedor)} €
              </div>
              <div style={{ fontSize: '12px', color: C.danger, marginTop: '8px', padding: '8px 10px', background: '#fde7e9', borderLeft: `3px solid ${C.danger}`, borderRadius: '2px' }}>
                <strong>O que vai acontecer:</strong><br/>
                • Item removido da encomenda do cliente (recalcula total)<br/>
                • Tr&acirc;nsitos revertidos no produto (-{link.quantidade_alocada} un)<br/>
                • Valor da compra fornecedor reduzido em {fmt2(valorFornecedor)} €<br/>
                • Lucro e Margens recalculam automaticamente
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmIndisp(false)} disabled={removingIndisp}
              style={{ padding: '6px 14px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: C.textMuted }}>
              Cancelar
            </button>
            <button onClick={removerPorIndisponivel} disabled={removingIndisp}
              style={{ padding: '6px 14px', background: C.danger, color: '#fff', border: 'none', borderRadius: '3px', cursor: removingIndisp ? 'wait' : 'pointer', fontSize: '12px', fontWeight: '700' }}>
              {removingIndisp ? 'A remover…' : 'Confirmar remoção'}
            </button>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <tr style={{ borderTop: `1px solid ${C.border}` }}>
      <td style={{ padding: '6px 8px', fontSize: '11px' }}>
        <strong style={{ color: C.primary }}>{link.encomenda?.numero_encomenda || '—'}</strong>
        <div style={{ color: C.textMuted, fontSize: '10px' }}>{link.encomenda?.cliente_nome}</div>
      </td>
      <td style={{ padding: '6px 8px', fontSize: '11px' }}>
        {link.produto_nome}
        <div style={{ color: C.textMuted, fontSize: '10px' }}>{link.quantidade_alocada} un.</div>
      </td>
      <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '11px' }}>
        {editing ? (
          <input value={custoPrev} onChange={e => setCustoPrev(e.target.value.replace(',', '.').replace(/[^0-9.]/g, ''))}
            placeholder="0.00" style={{ width: '70px', height: '24px', padding: '0 6px', border: `1px solid ${C.primary}`, borderRadius: '3px', fontSize: '11px', textAlign: 'right' }} />
        ) : (
          link.custo_unitario_previsao ? `${fmt2(link.custo_unitario_previsao)} €` : '—'
        )}
      </td>
      <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: '11px' }}>
        {editing ? (
          <input value={custoFinal} onChange={e => setCustoFinal(e.target.value.replace(',', '.').replace(/[^0-9.]/g, ''))}
            placeholder="0.00" style={{ width: '70px', height: '24px', padding: '0 6px', border: `1px solid ${C.success}`, borderRadius: '3px', fontSize: '11px', textAlign: 'right' }} />
        ) : (
          link.custo_unitario_final ? <span style={{ color: C.success, fontWeight: '700' }}>{fmt2(link.custo_unitario_final)} €</span> : '—'
        )}
      </td>
      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
        {editing ? (
          <>
            <button onClick={guardar} style={{ background: C.success, color: '#fff', border: 'none', padding: '3px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px', fontWeight: '700', marginRight: '3px' }}>OK</button>
            <button onClick={() => setEditing(false)} style={{ background: '#fff', border: `1px solid ${C.border}`, padding: '3px 8px', borderRadius: '3px', cursor: 'pointer', fontSize: '10px' }}>X</button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} title="Editar custos" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.primary, padding: '2px', marginRight: '2px' }}><Pencil size={11} /></button>
            <button onClick={() => setConfirmIndisp(true)} disabled={enviada}
              title={enviada ? 'Encomenda ja enviada — nao pode remover' : 'Remover por indisponibilidade do fornecedor (reverte transito + recalcula totais)'}
              style={{ background: 'transparent', border: 'none', cursor: enviada ? 'not-allowed' : 'pointer', color: enviada ? C.textMuted : C.warning, padding: '2px', marginRight: '2px', opacity: enviada ? 0.4 : 1 }}><PackageX size={11} /></button>
            <button onClick={remover} title="Remover link (so' apaga liga&ccedil;ao, nao reverte transito)" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.danger, padding: '2px' }}><Trash2 size={11} /></button>
          </>
        )}
      </td>
    </tr>
  )
}

function AdicionarLinkPanel({ compra, itens, jaLinkados, onClose, onAdded }) {
  const idsLinkados = new Set(jaLinkados.map(l => `${l.encomenda_id}-${l.produto_nome}`))
  // Auto-sugerir: encomendas cujo fornecedor_fatura_numero bate com a factura desta compra
  const numeroBate = (it) => (it.encomendas?.fornecedor_fatura_numero || '').trim() === (compra.numero_fatura || '').trim()
  const [seleccionados, setSeleccionados] = useState(() => {
    const auto = new Set()
    itens.forEach(it => { if (numeroBate(it) && !idsLinkados.has(`${it.encomenda_id}-${it.produto_nome}`)) auto.add(it.id) })
    return auto
  })
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)

  const filtrados = useMemo(() => {
    return itens.filter(it => {
      if (idsLinkados.has(`${it.encomenda_id}-${it.produto_nome}`)) return false
      if (!search) return true
      const s = search.toLowerCase()
      return (it.encomendas?.numero_encomenda || '').toLowerCase().includes(s)
        || (it.encomendas?.cliente_nome || '').toLowerCase().includes(s)
        || (it.produto_nome || '').toLowerCase().includes(s)
    })
  }, [itens, idsLinkados, search])

  const toggleSel = (id) => {
    setSeleccionados(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }

  const guardar = async () => {
    if (seleccionados.size === 0) { alert('Selecciona pelo menos uma linha.'); return }
    setSaving(true)
    const rows = []
    const encomendaIdsParaActualizar = new Set()
    for (const it of itens) {
      if (!seleccionados.has(it.id)) continue
      const { data: prod } = await supabase.from('Produtos').select('id').eq('Nome', it.produto_nome).limit(1).maybeSingle()
      rows.push({
        compra_id: compra.id,
        encomenda_id: it.encomenda_id,
        produto_id: prod?.id || null,
        produto_nome: it.produto_nome,
        quantidade_alocada: it.quantidade,
      })
      encomendaIdsParaActualizar.add(it.encomenda_id)
    }
    const { error } = await supabase.from('compras_fornecedor_encomendas').insert(rows)
    if (error) { setSaving(false); alert('Erro: ' + error.message); return }
    // Preencher fornecedor_fatura_numero nas encomendas linkadas (so se ainda vazio ou diferente)
    if (compra.numero_fatura && encomendaIdsParaActualizar.size > 0) {
      await supabase.from('encomendas')
        .update({ fornecedor_fatura_numero: compra.numero_fatura })
        .in('id', [...encomendaIdsParaActualizar])
    }
    setSaving(false)
    await onAdded()
  }

  return (
    <div style={{ marginTop: '10px', padding: '10px', background: C.surface, border: `1px solid ${C.primary}55`, borderRadius: '3px' }}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Procurar encomenda / cliente / produto"
          style={{ flex: 1, height: '28px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '12px' }} />
        <span style={{ fontSize: '11px', color: C.textMuted }}>{seleccionados.size} seleccionado(s)</span>
      </div>
      <div style={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: '3px' }}>
        {filtrados.length === 0 ? (
          <p style={{ padding: '12px', margin: 0, fontSize: '12px', color: C.textMuted, textAlign: 'center' }}>Nada para mostrar.</p>
        ) : filtrados.map(it => {
          const sel = seleccionados.has(it.id)
          const auto = numeroBate(it)
          return (
            <label key={it.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: sel ? '#eff6fc' : 'transparent', fontSize: '12px' }}>
              <input type="checkbox" checked={sel} onChange={() => toggleSel(it.id)} />
              <div style={{ flex: 1 }}>
                <div><strong style={{ color: C.primary }}>{it.encomendas?.numero_encomenda}</strong> · {it.encomendas?.cliente_nome}</div>
                <div style={{ color: C.textMuted, fontSize: '11px' }}>{it.produto_nome} — {it.quantidade} un.</div>
              </div>
              {auto && <span style={{ fontSize: '9px', padding: '1px 6px', background: C.success, color: '#fff', borderRadius: '8px', fontWeight: '700' }}>AUTO</span>}
            </label>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px', marginTop: '8px' }}>
        <button onClick={onClose} style={{ background: '#fff', border: `1px solid ${C.border}`, padding: '5px 12px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px' }}>Cancelar</button>
        <button onClick={guardar} disabled={saving || seleccionados.size === 0}
          style={{ background: C.primary, color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '3px', cursor: (saving || seleccionados.size === 0) ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '700', opacity: (saving || seleccionados.size === 0) ? 0.6 : 1 }}>
          {saving ? 'A guardar...' : `Linkar ${seleccionados.size}`}
        </button>
      </div>
    </div>
  )
}

// ─── Secao: Fase 2 (factura final com portes/ajustes) ──────────────────────
function SecaoFase2({ compra, onUpdated }) {
  const [open, setOpen] = useState(compra.fase === 'final' || !!compra.numero_fatura_final)
  const [form, setForm] = useState({
    numero_fatura_final: compra.numero_fatura_final || '',
    data_fatura_final: compra.data_fatura_final || '',
    valor_final_sem_iva: compra.valor_final_sem_iva || '',
    valor_final_iva: compra.valor_final_iva || '',
    valor_final_total: compra.valor_final_total || '',
  })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [parsedMsg, setParsedMsg] = useState('')
  const [parsedLinhas, setParsedLinhas] = useState(null) // linhas do PDF para analise de precos
  const [analiseModal, setAnaliseModal] = useState(null) // { linhas: [...], factura_nova }
  const [aplicando, setAplicando] = useState(false)
  const [erro, setErro] = useState('')

  const onValor = (campo, v) => {
    v = v.replace(',', '.').replace(/[^0-9.]/g, '')
    setForm(p => {
      const next = { ...p, [campo]: v }
      if (campo === 'valor_final_sem_iva' && v) {
        const s = Number(v); next.valor_final_iva = (s * 0.23).toFixed(2); next.valor_final_total = (s * 1.23).toFixed(2)
      } else if (campo === 'valor_final_total' && v) {
        const t = Number(v); next.valor_final_sem_iva = (t / 1.23).toFixed(2); next.valor_final_iva = (t - t / 1.23).toFixed(2)
      }
      return next
    })
  }

  const guardarFase2 = async () => {
    if (!form.numero_fatura_final.trim()) { setErro('Indica nº factura final.'); return }
    if (!form.valor_final_total || Number(form.valor_final_total) <= 0) { setErro('Valor final tem de ser > 0.'); return }
    setErro(''); setSaving(true)
    const facturaNova = form.numero_fatura_final.trim()
    const { error } = await supabase.from('compras_fornecedor').update({
      fase: 'final',
      numero_fatura_final: facturaNova,
      data_fatura_final: form.data_fatura_final || null,
      valor_final_sem_iva: Number(form.valor_final_sem_iva) || 0,
      valor_final_iva: Number(form.valor_final_iva) || 0,
      valor_final_total: Number(form.valor_final_total) || 0,
    }).eq('id', compra.id)
    setSaving(false)
    if (error) { setErro('Erro: ' + error.message); return }
    // Analise de precos: se temos linhas do parser, cruzar com produtos_internos
    if (Array.isArray(parsedLinhas) && parsedLinhas.length > 0) {
      await analisarPrecos(parsedLinhas, facturaNova)
    }
    await onUpdated()
  }

  // Compara linhas do PDF com produtos_internos e prepara preview de alteracoes
  const analisarPrecos = async (linhas, facturaNova) => {
    // Casa por EAN PRIMEIRO, e so depois por nome. O nome vem do texto do PDF e
    // basta o extractor falhar uma quebra de linha para trazer meia factura
    // agarrada ao artigo (IBAN, morada, condicoes de pagamento). Nesse caso nao
    // casa com nada, o sistema conclui "primeira vez" e propunha criar um
    // produto novo com esse nome monstruoso — enquanto o artigo verdadeiro
    // ficava sem o custo actualizado. O EAN e estavel e resolve isso.
    const eans  = [...new Set(linhas.map(l => l.ean).filter(Boolean).map(String))]
    const nomes = [...new Set(linhas.map(l => l.nome).filter(Boolean))]
    if (eans.length === 0 && nomes.length === 0) return

    const colsProd = 'Nome, EAN, preco, preco_manual, Email_Cliente'
    const [porEanRes, porNomeRes] = await Promise.all([
      eans.length  ? supabase.from('Produtos').select(colsProd).in('EAN', eans)   : Promise.resolve({ data: [] }),
      nomes.length ? supabase.from('Produtos').select(colsProd).in('Nome', nomes) : Promise.resolve({ data: [] }),
    ])
    const prodPorEan = new Map()
    for (const p of (porEanRes.data || [])) {
      const k = String(p.EAN || '')
      if (!k) continue
      if (!prodPorEan.has(k)) prodPorEan.set(k, [])
      prodPorEan.get(k).push(p)
    }
    // Nome canonico da linha: o do produto encontrado pelo EAN; se o EAN nao
    // der em nada, fica o nome que veio do PDF (comportamento anterior).
    const nomeReal = (l) => {
      const achados = prodPorEan.get(String(l.ean || ''))
      return (achados && achados.length) ? achados[0].Nome : l.nome
    }

    const nomesReais = [...new Set(linhas.map(nomeReal).filter(Boolean))]
    const piRes = nomesReais.length
      ? await supabase.from('produtos_internos').select('produto_nome, preco_fornecedor, preco_custo').in('produto_nome', nomesReais)
      : { data: [] }
    const piMap = new Map((piRes.data || []).map(r => [r.produto_nome, r]))
    const prodByNome = new Map()
    for (const p of [...(porEanRes.data || []), ...(porNomeRes.data || [])]) {
      if (!prodByNome.has(p.Nome)) prodByNome.set(p.Nome, [])
      const lista = prodByNome.get(p.Nome)
      // O mesmo produto pode vir pelas duas consultas — nao o duplicar.
      if (!lista.some(x => x.Email_Cliente === p.Email_Cliente && x.preco === p.preco)) lista.push(p)
    }
    // Descobrir factura antiga: ultima entrada em historico_precos_alteracoes ou preco_fornecedor actual (se existir)
    const alteracoes = []
    for (const l of linhas) {
      const nome = nomeReal(l)
      if (!nome) continue
      const custoBrutoNovo = Number(l.preco_unit_bruto || 0)
      const custoRealNovo = Number(l.custo_unit_real || 0)
      if (custoBrutoNovo <= 0) continue
      const pi = piMap.get(nome)
      const custoRealAntigo = pi?.preco_custo != null ? Number(pi.preco_custo) : null
      const deltaCusto = custoRealAntigo != null ? (custoRealNovo - custoRealAntigo) : null
      const primeiraVez = pi == null
      // Filtro: primeira vez OU |delta| >= 0.50 EUR
      if (!primeiraVez && (deltaCusto == null || Math.abs(deltaCusto) < 0.50)) continue
      const prodClientes = prodByNome.get(nome) || []
      alteracoes.push({
        produto_nome: nome,
        produto_ean: l.ean ? String(l.ean) : null,
        custo_bruto_novo: custoBrutoNovo,
        custo_real_novo: custoRealNovo,
        custo_real_antigo: custoRealAntigo,
        delta_custo: deltaCusto,
        primeira_vez: primeiraVez,
        subiu: !primeiraVez && deltaCusto > 0,
        desceu: !primeiraVez && deltaCusto < 0,
        clientes: prodClientes.map(p => ({
          email: p.Email_Cliente,
          preco_venda_actual: p.preco,
          modo: (p.preco_manual === false || p.preco_manual == null) ? 'auto' : 'manual',
        })),
      })
    }
    if (alteracoes.length === 0) return
    setAnaliseModal({ linhas: alteracoes, factura_nova: facturaNova })
  }

  const confirmarAplicarAlteracoes = async () => {
    if (!analiseModal) return
    setAplicando(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${SUPABASE_URL}/functions/v1/aplicar-alteracoes-precos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({
          compra_id: compra.id,
          factura_nova: analiseModal.factura_nova,
          linhas: analiseModal.linhas.map(l => ({
            produto_nome: l.produto_nome,
            produto_ean: l.produto_ean,
            custo_bruto_novo: l.custo_bruto_novo,
            custo_real_novo: l.custo_real_novo,
            aplicar_venda: true,
          })),
        }),
      })
      const data = await res.json()
      if (!data.ok) throw new Error(data.erro || 'erro')
      alert(`${data.aplicadas} alteracoes aplicadas. Email enviado aos admins.`)
      setAnaliseModal(null)
      await onUpdated()
    } catch (e) {
      alert('Erro ao aplicar: ' + (e?.message || String(e)))
    } finally {
      setAplicando(false)
    }
  }

  const uploadFaturaFinal = async (file) => {
    if (!file) return
    setUploading(true)
    setParsedMsg('')
    setErro('')
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase()
      const path = `${compra.id}/fatura-final.${ext}`
      const { error: upErr } = await supabase.storage.from('faturas-fornecedor').upload(path, file, { upsert: true, contentType: file.type })
      if (upErr) throw new Error(upErr.message)
      const { error } = await supabase.from('compras_fornecedor').update({ fatura_final_pdf_url: path, fatura_final_em: new Date().toISOString() }).eq('id', compra.id)
      if (error) throw new Error(error.message)
      // Tenta extrair dados do PDF (parser PLUS SKILL) e auto-preenche os campos
      if (ext === 'pdf') {
        setParsing(true)
        try {
          const result = await parseFaturaPdfFile(file)
          if (result.ok) {
            setForm(p => ({
              ...p,
              numero_fatura_final: result.numero_fatura || p.numero_fatura_final,
              data_fatura_final: result.data_fatura || p.data_fatura_final,
              valor_final_sem_iva: String(result.valor_sem_iva.toFixed(2)),
              valor_final_iva: '0',
              valor_final_total: String(result.valor_total.toFixed(2)),
            }))
            if (Array.isArray(result.linhas)) setParsedLinhas(result.linhas)
            setParsedMsg(`Dados extraidos do PDF: factura ${result.numero_fatura || '?'} · total ${result.valor_total.toFixed(2)} EUR · ${(result.linhas || []).length} linhas. Verifica e confirma.`)
          } else {
            setParsedMsg('Parser nao conseguiu extrair tudo automaticamente — preenche manualmente.')
          }
        } catch (parseErr) {
          setParsedMsg('Nao foi possivel ler o PDF automaticamente — preenche manualmente.')
        } finally {
          setParsing(false)
        }
      }
      await onUpdated()
    } catch (e) { alert('Erro: ' + e.message) }
    finally { setUploading(false) }
  }

  const verDoc = async (path) => {
    if (!path) return
    const { data } = await supabase.storage.from('faturas-fornecedor').createSignedUrl(path, 60 * 10)
    if (data?.signedUrl) window.open(data.signedUrl, '_blank')
  }

  const diff = (Number(compra.valor_final_total || 0) - Number(compra.valor_total || 0))

  return (
    <div style={{ border: `2px solid ${compra.fase === 'final' ? C.success : C.warning + '66'}`, borderRadius: '4px', padding: '12px 14px', background: compra.fase === 'final' ? '#e8f5e9' : '#fff8e1' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <p style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: compra.fase === 'final' ? C.success : C.warning, textTransform: 'uppercase' }}>
          {compra.fase === 'final' ? '✅ FASE 2 — Factura final registada' : '⏳ FASE 2 — Factura final (valor definitivo + ajustes do fornecedor)'}
        </p>
        <span style={{ fontSize: '11px', color: C.textMuted }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && (
        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {erro && <p style={{ margin: 0, padding: '6px 10px', background: '#fde7e9', border: `1px solid ${C.danger}`, borderRadius: '3px', fontSize: '11px', color: C.danger }}>{erro}</p>}
          {parsedMsg && <p style={{ margin: 0, padding: '6px 10px', background: '#e8f5e9', border: `1px solid ${C.success}`, borderRadius: '3px', fontSize: '11px', color: C.success }}>📄 {parsedMsg}</p>}
          <p style={{ margin: 0, fontSize: '11px', color: C.text }}>
            Quando o fornecedor enviar a factura definitiva (~18 dias após a 1ª, com portes/ajustes), regista-a aqui.
            O lucro <strong>previsão</strong> é então substituído por <strong>lucro final</strong>.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <Campo label="Nº factura final">
              <input value={form.numero_fatura_final} onChange={e => setForm(p => ({ ...p, numero_fatura_final: e.target.value }))} placeholder="ex: FA-2026/189" style={inputStyle()} />
            </Campo>
            <Campo label="Data factura final">
              <input type="date" value={form.data_fatura_final} onChange={e => setForm(p => ({ ...p, data_fatura_final: e.target.value }))} style={inputStyle()} />
            </Campo>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
            <Campo label="S/IVA (€)">
              <input value={form.valor_final_sem_iva} onChange={e => onValor('valor_final_sem_iva', e.target.value)} placeholder="0.00" style={inputStyle()} />
            </Campo>
            <Campo label="IVA (€)">
              <input value={form.valor_final_iva} onChange={e => onValor('valor_final_iva', e.target.value)} placeholder="0.00" style={inputStyle()} />
            </Campo>
            <Campo label="Total c/IVA (€)">
              <input value={form.valor_final_total} onChange={e => onValor('valor_final_total', e.target.value)} placeholder="0.00" style={{ ...inputStyle(), fontWeight: '700', color: C.success }} />
            </Campo>
          </div>
          {compra.valor_total > 0 && Number(form.valor_final_total) > 0 && (
            <p style={{ margin: 0, padding: '6px 10px', background: '#fff', border: `1px solid ${C.border}`, borderRadius: '3px', fontSize: '11px', color: C.text }}>
              Previsão: {fmt2(compra.valor_total)} € · Final: {fmt2(form.valor_final_total)} € · Diferença: <strong style={{ color: diff > 0 ? C.danger : C.success }}>{diff > 0 ? '+' : ''}{fmt2(diff)} €</strong>
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {compra.fatura_final_pdf_url ? (
              <button onClick={() => verDoc(compra.fatura_final_pdf_url)} style={{ background: C.success, color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer', fontSize: '11px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Eye size={11} /> Ver factura final</button>
            ) : null}
            <label style={{ background: compra.fatura_final_pdf_url ? '#fff' : C.primary, color: compra.fatura_final_pdf_url ? C.primary : '#fff', border: `1px solid ${C.primary}`, padding: '5px 10px', borderRadius: '3px', cursor: (uploading || parsing) ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <Upload size={11} /> {parsing ? 'A ler PDF...' : (uploading ? 'A enviar...' : (compra.fatura_final_pdf_url ? 'Substituir PDF' : 'Anexar PDF final'))}
              <input type="file" accept="application/pdf,image/*" style={{ display: 'none' }} disabled={uploading || parsing}
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadFaturaFinal(f); e.target.value = '' }} />
            </label>
            <button onClick={guardarFase2} disabled={saving}
              style={{ background: C.success, color: '#fff', border: 'none', padding: '5px 12px', borderRadius: '3px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '11px', fontWeight: '700', marginLeft: 'auto' }}>
              {saving ? 'A guardar...' : 'Confirmar Fase 2'}
            </button>
          </div>
        </div>
      )}
      {analiseModal && (
        <div onClick={() => !aplicando && setAnaliseModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '8px', maxWidth: '1100px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: 0, fontSize: '11px', color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comparacao de precos</p>
                <h2 style={{ margin: '2px 0 0', fontSize: '18px', color: C.text }}>Factura {analiseModal.factura_nova} — {analiseModal.linhas.length} alteracao(oes) detectada(s)</h2>
              </div>
              <button onClick={() => !aplicando && setAnaliseModal(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: C.textMuted }}>×</button>
            </div>
            <div style={{ padding: '16px 20px', overflow: 'auto', flex: 1 }}>
              <p style={{ fontSize: '12px', color: C.textMuted, margin: '0 0 12px' }}>
                Filtro aplicado: alteracoes de custo &ge; 0.50€ (absoluto). <strong style={{ color: '#a4262c' }}>Subidas</strong> actualizam preco venda em produtos AUTO. <strong style={{ color: '#107c10' }}>Descidas</strong> so actualizam custo. Produtos <strong style={{ color: '#c07400' }}>manuais</strong> apenas avisam.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f3f2f1' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase' }}>Produto</th>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase' }}>Clientes</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase' }}>Custo antigo</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase' }}>Custo novo</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase' }}>&Delta;</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '10px', color: C.textMuted, textTransform: 'uppercase' }}>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analiseModal.linhas.map((l, i) => {
                      const cor = l.primeira_vez ? '#0078d4' : l.subiu ? '#a4262c' : '#107c10'
                      const motivo = l.primeira_vez ? '🆕 Primeira vez' : l.subiu ? '🔺 Subida' : '🔻 Descida'
                      const clientesInfo = l.clientes.length === 0
                        ? <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Sem Produtos no catalogo</span>
                        : l.clientes.map((c, j) => (
                            <div key={j} style={{ fontSize: '11px', marginBottom: '2px' }}>
                              <span style={{ background: c.modo === 'auto' ? '#dff6dd' : '#fff4ce', color: c.modo === 'auto' ? '#107c10' : '#c07400', padding: '1px 5px', borderRadius: '6px', fontSize: '9px', fontWeight: '700', marginRight: '4px' }}>{c.modo.toUpperCase()}</span>
                              {c.email} <span style={{ color: C.textMuted }}>· venda actual {fmt2(c.preco_venda_actual)}€</span>
                            </div>
                          ))
                      return (
                        <tr key={i}>
                          <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
                            <div style={{ fontWeight: '600' }}>{l.produto_nome}</div>
                            <div style={{ fontSize: '10px', color: C.textMuted, fontFamily: 'monospace' }}>{l.produto_ean || '-'}</div>
                          </td>
                          <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>{clientesInfo}</td>
                          <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, textAlign: 'right', color: C.textMuted }}>{l.custo_real_antigo != null ? fmt2(l.custo_real_antigo) + ' €' : '—'}</td>
                          <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, textAlign: 'right', fontWeight: '700', color: cor }}>{fmt2(l.custo_real_novo)} €</td>
                          <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, textAlign: 'right', color: cor, fontWeight: '700' }}>{l.delta_custo != null ? (l.delta_custo > 0 ? '+' : '') + fmt2(l.delta_custo) + ' €' : '—'}</td>
                          <td style={{ padding: '8px 10px', borderBottom: `1px solid ${C.border}`, textAlign: 'center', color: cor, fontSize: '11px' }}>{motivo}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setAnaliseModal(null)} disabled={aplicando}
                style={{ background: '#fff', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: '4px', cursor: aplicando ? 'not-allowed' : 'pointer', fontSize: '13px', color: C.text }}>Cancelar</button>
              <button onClick={confirmarAplicarAlteracoes} disabled={aplicando}
                style={{ background: C.primary, color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: aplicando ? 'wait' : 'pointer', fontSize: '13px', fontWeight: '700' }}>
                {aplicando ? 'A aplicar…' : `Confirmar ${analiseModal.linhas.length} alteracao(oes)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
